/**
 * Blocky Striker — self-contained 3D voxel soccer endless-runner runtime hosted in a
 * react-native-webview (native) / srcDoc iframe (web). Recreated at high fidelity from the
 * design handoff (specs/blocky-striker/handoff/README.md — the app copy here is authoritative,
 * the handoff is a frozen reference; specs/blocky-striker/spec.md).
 *
 * Why a string module: like every Warmup game runtime, the whole game ships as ONE HTML
 * document with zero RN-bundle imports and zero network dependencies — it always boots
 * offline. three.js is therefore INLINED (blockyStrikerThree.vendor.ts, tree-shaken IIFE;
 * regenerate via scripts/build-blocky-striker-three.mjs). Avatar thumbnails load from the CDN
 * as progressive enhancement (colored-circle fallback) — boot never waits on them. The
 * wordmark / HUD coin / game-over monkey are inlined data URIs (blockyStrikerInlineAssets.ts).
 *
 * Bridge protocol (window.ReactNativeWebView.postMessage, window.parent fallback; JSON):
 *   web → RN:  { type:'ready' }
 *              | { type:'run-end', points, coins, goals, distance, cause:'time'|'lives' }
 *              | { type:'load-error' }
 *   RN → web:  none (host ref supports reload only)
 *
 * Scoring/collision predicates are shared with Vitest via blockyStrikerScoring.ts (embedded
 * as source — §9 T-12 kick-only goal rule is tested there, not re-implemented here).
 *
 * Modern-three fidelity notes: ColorManagement is disabled and the renderer outputs linear —
 * the legacy (r128) color pipeline the handoff was tuned on. Fonts fall back from Inter to the
 * system stack (no Google Fonts fetch — offline rule).
 *
 * Copy guard: no "bet" (pick/prediction wording), no real team/player/tournament names,
 * no "unlimited".
 */

import { getAssetUrl, getSmallAssetUrl } from '../../../constants/assets';
import {
  BLOCKY_STRIKER_ROSTER,
  type BlockyStrikerRosterEntry,
} from '../constants/blockyStriker.constants';
import {
  BLOCKY_STRIKER_WORDMARK_URI,
  BLOCKY_STRIKER_COIN_URI,
  BLOCKY_STRIKER_MONKEY_WIN_URI,
} from './blockyStrikerInlineAssets';
import {
  BLOCKY_STRIKER_LEVELS,
  BLOCKY_STRIKER_MISSIONS,
  BLOCKY_STRIKER_OBSTACLES,
  BLOCKY_STRIKER_SCORING,
  getBlockyStrikerScoringSource,
} from './blockyStrikerScoring';
import { BLOCKY_STRIKER_THREE_SRC } from './blockyStrikerThree.vendor';

/** Runtime-facing roster row: resolved thumbnail URL + the five voxel colors. */
function toRuntimeRoster(entry: BlockyStrikerRosterEntry) {
  return {
    id: entry.id,
    name: entry.name,
    img: entry.thumbSmall ? getSmallAssetUrl(entry.thumbPath) : getAssetUrl(entry.thumbPath),
    pal: entry.palette,
  };
}

export function buildBlockyStrikerGameHtml(): string {
  // A closing script tag inside inlined JS would terminate our <script> block early.
  const threeSrc = BLOCKY_STRIKER_THREE_SRC.replace(/<\/script/gi, '<\\/script');
  const rosterJson = JSON.stringify(BLOCKY_STRIKER_ROSTER.map(toRuntimeRoster));
  const scoringJson = JSON.stringify(BLOCKY_STRIKER_SCORING);
  const obstaclesJson = JSON.stringify(BLOCKY_STRIKER_OBSTACLES);
  const missionsJson = JSON.stringify(BLOCKY_STRIKER_MISSIONS);
  const levelsJson = JSON.stringify(BLOCKY_STRIKER_LEVELS);
  const scoringSrc = getBlockyStrikerScoringSource();

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
<title>Blocky Striker</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
  /* touch-action:none must NOT be on html/body — it blocks touch scrolling of the start/over
     overlays on mobile. It lives on the game surfaces (#stage for drag-steer/swipe, .ctl buttons). */
  html,body { width:100%; height:100%; overflow:hidden; background:#0F0A1A;
    font-family:'Inter',-apple-system,system-ui,sans-serif; user-select:none; -webkit-user-select:none; }
  #stage { position:absolute; inset:0; touch-action:none; }
  #stage canvas { display:block; }
  .tab { position:absolute; inset:0; }
  .hidden { display:none !important; }

  /* ---- shared overlay chrome (handoff §1/§3) ---- */
  /* No align/justify centering here: with a card taller than the screen, flex centering clips the
     card's top ABOVE the scroll origin (unreachable). margin:auto on the card centers when it fits
     and scrolls from the very top when it doesn't. */
  .overlay { position:absolute; inset:0; display:flex;
    background:rgba(15,10,26,.82); backdrop-filter:blur(6px); overflow:auto; overflow-x:hidden;
    padding:20px; touch-action:pan-y; -webkit-overflow-scrolling:touch; }
  .card { background:#1F2937; border-radius:16px; padding:32px; width:100%; margin:auto;
    box-shadow:0 6px 12px rgba(0,0,0,.5); display:flex; flex-direction:column; align-items:center; }
  .card.start { max-width:520px; gap:20px; }
  .card.over { max-width:420px; gap:18px; }
  @media (max-width: 480px) {
    .overlay { padding:16px; }
    .card { padding:20px; border-radius:12px; box-sizing:border-box; }
    .card.start { max-width:100%; width:100%; }
    .card.over { max-width:100%; width:100%; }
    .card>div, .card>span { max-width:100%; }
    .title { font-size:20px; }
    .subtitle { font-size:12px; }
    .btn-primary { min-height:48px; font-size:16px; }
    .btn-secondary { min-height:40px; font-size:14px; }
    .section-label { font-size:12px; margin-bottom:8px; }
    .avatar-grid { gap:6px; }
    .avatar-thumb { width:40px; height:40px; }
    .avatar-name { font-size:9px; }
    .legend { gap:8px; font-size:11px; }
    .stat-val { font-size:22px; }
    .stat-label { font-size:11px; }
    .mode-row { gap:6px; }
    .level-grid { gap:6px; grid-template-columns:repeat(3,1fr); }
    .level-num { font-size:13px; }
  }
  .wordmark { filter:brightness(10); animation:floaty 3s ease-in-out infinite; }
  .title { font-size:24px; font-weight:800; color:#F7FAFC; letter-spacing:.5px; text-align:center; }
  .subtitle { font-size:14px; color:#9CA3AF; margin-top:6px; text-align:center; }
  .section-label { font-size:13px; font-weight:600; color:#D1D5DB; margin-bottom:10px;
    text-transform:uppercase; letter-spacing:1px; }
  /* minmax(0,1fr): plain 1fr tracks refuse to shrink below the longest avatar name's min-content
     width, overflowing the card to the right on narrow screens. */
  .avatar-grid { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:8px; width:100%; }
  .avatar-cell { cursor:pointer; border-radius:12px; padding:6px; display:flex; flex-direction:column;
    align-items:center; gap:4px; background:transparent; border:2px solid #374151; min-width:0; }
  .avatar-cell:hover { background:#374151; }
  .avatar-cell.sel { background:rgba(167,139,250,.18); border-color:#A78BFA; }
  .avatar-thumb { width:44px; height:44px; border-radius:9999px; background-color:#111827;
    background-size:cover; background-position:center; }
  .avatar-name { font-size:10px; color:#9CA3AF; font-weight:500; text-align:center; line-height:1.1;
    max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .btn-primary { width:100%; background:#A78BFA; color:#111827; border:none; border-radius:10px;
    min-height:52px; font-size:17px; font-weight:700; font-family:inherit; cursor:pointer;
    box-shadow:0 2px 4px rgba(167,139,250,.3); }
  .btn-primary:hover { background:#C4B5FD; }
  .btn-primary:active { background:#8B5CF6; }
  .btn-secondary { width:100%; background:transparent; color:#D1D5DB; border:1.5px solid #4B5563;
    border-radius:10px; min-height:44px; font-size:15px; font-weight:600; font-family:inherit; cursor:pointer; }
  .btn-secondary:hover { border-color:#9CA3AF; }
  .legend { display:flex; gap:16px; flex-wrap:wrap; justify-content:center; font-size:12px; color:#9CA3AF; }
  .legend b { color:#D1D5DB; }
  .best-line { font-size:13px; color:#FCD34D; font-weight:600; }

  /* ---- game over ---- */
  .stat-row { display:flex; gap:12px; width:100%; }
  .stat { flex:1; background:#374151; border-radius:12px; padding:14px; text-align:center; }
  .stat-val { font-size:26px; font-weight:800; font-variant-numeric:tabular-nums; color:#F7FAFC; }
  .stat-val.gold { color:#FCD34D; } .stat-val.mint { color:#6EE7B7; }
  .stat-label { font-size:12px; color:#9CA3AF; font-weight:500; }
  .new-best { background:#FCD34D; color:#111827; border-radius:9999px; padding:6px 20px;
    font-size:13px; font-weight:700; white-space:nowrap; }

  /* ---- HUD (handoff §2) ---- */
  #hud { pointer-events:none; }
  .topbar { position:absolute; top:0; left:0; right:0; display:flex; align-items:center;
    justify-content:space-between; gap:12px; padding:calc(12px + env(safe-area-inset-top)) 16px 12px 16px; }
  .pill { display:flex; align-items:center; background:rgba(31,41,55,.85); border-radius:9999px;
    padding:8px 14px; box-shadow:0 2px 4px rgba(0,0,0,.4); }
  .hud-group { display:flex; align-items:center; gap:10px; }
  .hud-id { gap:8px; }
  .hud-avatar { width:28px; height:28px; border-radius:9999px; background-color:#374151;
    background-size:cover; background-position:center; }
  .hud-coin { gap:6px; }
  .hud-coin img { width:20px; height:20px; }
  .hud-coin span { color:#FCD34D; font-weight:700; font-size:16px; min-width:28px; }
  .hud-center { display:flex; flex-direction:column; align-items:center; gap:4px; }
  .timer-pill { background:#111827; border-radius:9999px; padding:8px 20px; box-shadow:0 2px 4px rgba(0,0,0,.4); }
  .timer-pill span { font-size:22px; font-weight:800; font-variant-numeric:tabular-nums; color:#FCD34D; }
  .timer-pill.low span { color:#F87171; animation:pulseLow 1s ease-in-out infinite; }
  .dist { color:#9CA3AF; font-size:13px; font-weight:500; }
  .hearts { gap:4px; }
  .hearts span { font-size:18px; line-height:1; color:#F87171; }
  .hearts span.spent { color:#4B5563; }
  .score-pill { padding:8px 16px; }
  .score-pill .pts { color:#F7FAFC; font-weight:700; font-size:16px; font-variant-numeric:tabular-nums; }
  .score-pill .unit { color:#9CA3AF; font-size:12px; font-weight:500; }

  .controls { position:absolute; bottom:0; left:0; right:0; display:flex;
    justify-content:space-between; align-items:flex-end; padding:22px 22px calc(22px + env(safe-area-inset-bottom)); }
  .cluster { display:flex; gap:14px; }
  .ctl { pointer-events:auto; width:88px; height:88px; border-radius:9999px; border:3px solid;
    display:flex; flex-direction:column; align-items:center; justify-content:center; color:#fff;
    cursor:pointer; user-select:none; touch-action:none; box-shadow:0 4px 14px rgba(0,0,0,.5); }
  .ctl .glyph { font-size:28px; line-height:1; }
  .ctl .lbl { font-size:11px; font-weight:800; letter-spacing:1px; }
  .ctl.steer { background:rgba(167,139,250,.28); border-color:#A78BFA; font-size:38px; }
  .ctl.steer.active { background:#A78BFA; }
  .ctl.jump { background:rgba(110,231,183,.28); border-color:#6EE7B7; }
  .ctl.jump.active { background:#6EE7B7; }
  .ctl.kick { background:rgba(252,211,77,.28); border-color:#FCD34D; }
  .ctl.kick.active { background:#FCD34D; }
  /* v2: JUMP + SLIDE stacked in the single 88px footprint the JUMP button used (AC-17) */
  .ctl-stack { width:88px; height:88px; display:flex; flex-direction:column; gap:6px; }
  .ctl.half { width:88px; height:41px; border-radius:22px; flex-direction:row; gap:6px; border-width:2px; }
  .ctl.half .glyph { font-size:18px; }
  .ctl.half .lbl { font-size:10px; }
  .ctl.slide { background:rgba(96,165,250,.28); border-color:#60A5FA; }
  .ctl.slide.active { background:#60A5FA; }
  @media (max-width: 420px) {
    .ctl { width:76px; height:76px; border-width:2.5px; }
    .ctl .glyph { font-size:24px; }
    .ctl.steer { font-size:32px; }
    .ctl.half { width:76px; height:36px; }
    .ctl.half .glyph { font-size:16px; }
    .ctl-stack { width:76px; height:76px; gap:4px; }
    .cluster { gap:10px; }
    .controls { padding:16px 16px calc(16px + env(safe-area-inset-bottom)); }
  }

  /* ---- v3: power-up chips, combo, pause/sound, missions, trails, revive ---- */
  .chips { position:absolute; top:64px; left:0; right:0; display:flex; justify-content:center; gap:8px; pointer-events:none; }
  .chip { background:rgba(31,41,55,.85); border-radius:9999px; padding:4px 12px; font-size:12px; font-weight:800; letter-spacing:.5px; box-shadow:0 2px 4px rgba(0,0,0,.4); }
  .chip.magnet { color:#F87171; } .chip.shield { color:#60A5FA; } .chip.x2 { color:#FCD34D; }
  .combo { color:#FCD34D; font-weight:800; font-size:13px; margin-right:6px; }
  .iconbtn { pointer-events:auto; position:absolute; width:38px; height:38px; border-radius:9999px;
    background:rgba(31,41,55,.85); color:#F7FAFC; display:flex; align-items:center; justify-content:center;
    font-size:15px; cursor:pointer; box-shadow:0 2px 4px rgba(0,0,0,.4); user-select:none; }
  #btnPause { top:64px; left:16px; }
  #btnSound { top:64px; left:62px; }
  .missions { width:100%; background:#111827; border-radius:12px; padding:12px 14px; display:flex; flex-direction:column; gap:6px; }
  .mission { display:flex; justify-content:space-between; gap:10px; font-size:12px; color:#D1D5DB; }
  .mission.done { color:#6EE7B7; }
  .mission .prog { color:#9CA3AF; font-variant-numeric:tabular-nums; white-space:nowrap; }
  .trail-row { display:flex; gap:8px; justify-content:center; }
  .trail { width:34px; height:34px; border-radius:9999px; border:2px solid #374151; background:#111827;
    display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#9CA3AF; cursor:pointer; }
  .trail.sel { border-color:#A78BFA; box-shadow:0 0 0 2px rgba(167,139,250,.35); }
  .trail.locked { opacity:.55; cursor:default; }
  .revive-count { font-size:44px; font-weight:800; color:#FCD34D; font-variant-numeric:tabular-nums; }

  /* ---- v4: level campaign ---- */
  .mode-row { display:flex; gap:8px; width:100%; }
  .mode-btn { flex:1; background:transparent; border:2px solid #374151; border-radius:10px; min-height:38px;
    color:#D1D5DB; font-size:13px; font-weight:700; font-family:inherit; cursor:pointer; }
  .mode-btn.sel { border-color:#A78BFA; background:rgba(167,139,250,.18); color:#F7FAFC; }
  .level-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; width:100%; margin-top:8px; }
  .level-cell { border:2px solid #374151; border-radius:10px; padding:6px 4px; display:flex; flex-direction:column;
    align-items:center; gap:2px; cursor:pointer; background:#111827; }
  .level-cell.sel { border-color:#A78BFA; background:rgba(167,139,250,.18); }
  .level-cell.locked { opacity:.5; cursor:default; }
  .level-num { font-size:15px; font-weight:800; color:#F7FAFC; }
  .level-stars { font-size:10px; color:#FCD34D; letter-spacing:1px; min-height:12px; }
  .level-obj { font-size:12px; color:#9CA3AF; text-align:center; margin-top:6px; }
  .over-stars { font-size:30px; color:#FCD34D; letter-spacing:6px; }

  .hint { position:absolute; bottom:120px; left:50%; transform:translateX(-50%);
    background:rgba(17,24,39,.9); color:#F7FAFC; font-size:14px; font-weight:600;
    padding:10px 20px; border-radius:9999px; box-shadow:0 4px 12px rgba(0,0,0,.5);
    animation:pulseLow 1.4s ease-in-out infinite; white-space:nowrap; }
  @media (max-width: 420px) {
    .topbar { gap:8px; padding:calc(8px + env(safe-area-inset-top)) 12px 8px 12px; }
    .pill { padding:6px 10px; font-size:13px; }
    .hud-avatar { width:24px; height:24px; }
    .hud-coin img { width:16px; height:16px; }
    .hud-coin span { font-size:14px; }
    .timer-pill { padding:6px 14px; }
    .timer-pill span { font-size:18px; }
    .dist { font-size:11px; }
    .score-pill .pts { font-size:14px; }
    .score-pill .unit { font-size:10px; }
    .hearts span { font-size:16px; }
    .iconbtn { width:32px; height:32px; font-size:13px; }
    #btnPause { top:52px; }
    #btnSound { top:52px; left:52px; }
    .hint { font-size:12px; padding:8px 14px; bottom:110px; }
    .chips { top:48px; gap:6px; }
    .chip { padding:2px 8px; font-size:10px; }
  }
  .toast { position:absolute; top:22%; left:50%; transform:translateX(-50%);
    animation:toastPop 1.6s ease forwards; color:#111827; font-weight:800; font-size:26px;
    letter-spacing:1px; padding:10px 26px; border-radius:9999px;
    box-shadow:0 6px 12px rgba(0,0,0,.5); white-space:nowrap; }

  @keyframes toastPop { 0%{transform:translate(-50%,-10px) scale(.7);opacity:0}
    15%{transform:translate(-50%,0) scale(1.08);opacity:1} 25%{transform:translate(-50%,0) scale(1)}
    80%{opacity:1} 100%{opacity:0;transform:translate(-50%,-14px)} }
  @keyframes pulseLow { 0%,100%{opacity:1} 50%{opacity:.45} }
  @keyframes floaty { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  @media (prefers-reduced-motion: reduce) {
    .wordmark, .hint, .timer-pill.low span, .over-art { animation:none !important; }
  }
</style>
</head>
<body>
<div id="stage"></div>

<div id="hud" class="tab hidden">
  <div class="topbar">
    <div class="hud-group">
      <div class="pill hud-id">
        <div id="hudAvatar" class="hud-avatar"></div>
        <img src="${BLOCKY_STRIKER_WORDMARK_URI}" alt="Zporti" style="height:16px; filter:brightness(10);" />
      </div>
      <div class="pill hud-coin">
        <img src="${BLOCKY_STRIKER_COIN_URI}" alt="coin" />
        <span id="hudCoins">0</span>
      </div>
    </div>
    <div class="hud-center">
      <div id="timerPill" class="timer-pill"><span id="hudTime">1:00</span></div>
      <span id="hudDist" class="dist">0 m</span>
      <span id="hudObjective" class="dist hidden"></span>
    </div>
    <div class="hud-group">
      <div id="hudHearts" class="pill hearts"></div>
      <div class="pill score-pill"><span id="hudCombo" class="combo"></span><span id="hudScore" class="pts">0</span><span class="unit"> PTS</span></div>
    </div>
  </div>
  <div id="btnPause" class="iconbtn">&#10074;&#10074;</div>
  <div id="btnSound" class="iconbtn">&#128266;</div>
  <div id="chips" class="chips"></div>
  <div class="controls">
    <div class="cluster">
      <div id="btnLeft" class="ctl steer">◀</div>
      <div id="btnRight" class="ctl steer">▶</div>
    </div>
    <div class="cluster">
      <div class="ctl-stack">
        <div id="btnJump" class="ctl half jump"><span class="glyph">⤒</span><span class="lbl">JUMP</span></div>
        <div id="btnSlide" class="ctl half slide"><span class="glyph">⤓</span><span class="lbl">SLIDE</span></div>
      </div>
      <div id="btnKick" class="ctl kick"><span class="glyph">⚽</span><span class="lbl">KICK</span></div>
    </div>
  </div>
  <div id="hint" class="hint hidden">Buttons or arrow keys &nbsp;·&nbsp; swipe ⤒ jump / ⤓ slide</div>
  <div id="toastMount"></div>
</div>

<div id="startScreen" class="overlay">
  <div class="card start">
    <img class="wordmark" src="${BLOCKY_STRIKER_WORDMARK_URI}" alt="Zporti" style="height:34px;" />
    <div>
      <div class="title">BLOCKY STRIKER</div>
      <div class="subtitle">Dribble the pitch. Dodge the blocks. Bury it in the net.</div>
    </div>
    <div style="width:100%;">
      <div class="section-label">Pick your player</div>
      <div id="avatarGrid" class="avatar-grid"></div>
    </div>
    <div style="width:100%;">
      <div class="section-label">Mode</div>
      <div class="mode-row">
        <button id="modeEndless" class="mode-btn sel">∞ &nbsp;Endless</button>
        <button id="modeLevels" class="mode-btn">🏁 &nbsp;Levels</button>
      </div>
      <div id="levelGrid" class="level-grid hidden"></div>
      <div id="levelObjLine" class="level-obj hidden"></div>
    </div>
    <div style="width:100%;">
      <div class="section-label">Today's missions</div>
      <div id="missionsPanel" class="missions"></div>
    </div>
    <div style="width:100%;">
      <div class="section-label">Trail</div>
      <div id="trailRow" class="trail-row"></div>
    </div>
    <button id="btnKickOff" class="btn-primary">▶ &nbsp;Kick Off</button>
    <div class="legend">
      <span><b>← → / A D</b> steer</span>
      <span><b>W / ↑</b> jump</span>
      <span><b>S / ↓</b> slide</span>
      <span><b>Space / X</b> kick</span>
      <span style="color:#FCD34D;">● run + coins = points</span>
      <span style="color:#6EE7B7;">⏱ +time &nbsp;♥ +life</span>
    </div>
    <div id="bestLine" class="best-line hidden"></div>
  </div>
</div>

<div id="overScreen" class="overlay hidden">
  <div class="card over">
    <img class="over-art wordmark" src="${BLOCKY_STRIKER_MONKEY_WIN_URI}" alt="Zporti monkey"
      style="width:96px; height:96px; object-fit:contain; filter:none;" />
    <div id="overTitle" class="title" style="font-size:22px; white-space:nowrap;">TIME'S UP!</div>
    <div id="overStars" class="over-stars hidden"></div>
    <div class="stat-row">
      <div class="stat"><div id="overPoints" class="stat-val">0</div><div class="stat-label">POINTS</div></div>
      <div class="stat"><div id="overCoins" class="stat-val gold">0</div><div class="stat-label">COINS</div></div>
      <div class="stat"><div id="overGoals" class="stat-val mint">0</div><div class="stat-label">GOALS</div></div>
    </div>
    <div id="newBest" class="new-best hidden">★ NEW BEST</div>
    <button id="btnAgain" class="btn-primary">Play Again</button>
    <button id="btnMenu" class="btn-secondary">Change Player</button>
  </div>
</div>

<div id="reviveScreen" class="overlay hidden">
  <div class="card over">
    <div class="title">KEEP RUNNING?</div>
    <div id="reviveCount" class="revive-count">3</div>
    <button id="btnRevive" class="btn-primary">⚡ &nbsp;Free Revive</button>
    <button id="btnGiveUp" class="btn-secondary">End Run</button>
  </div>
</div>

<div id="pauseScreen" class="overlay hidden">
  <div class="card over">
    <div class="title">PAUSED</div>
    <button id="btnResume" class="btn-primary">▶ &nbsp;Resume</button>
  </div>
</div>

<script>
window.__errs = [];
window.onerror = function(m, s, l, c, e){ window.__errs.push(String(m) + ' @' + l + ':' + c); };
// Bridge: native WebView first, srcDoc-iframe parent as the web fallback (same contract as siblings).
window.__post = function(m){
  var s = JSON.stringify(m);
  try { if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) { window.ReactNativeWebView.postMessage(s); return; } } catch (e) {}
  try { if (window.parent && window.parent !== window) window.parent.postMessage(s, '*'); } catch (e) {}
};
</script>
<script>${threeSrc}</script>
<script>
${scoringSrc}
</script>
<script>
(function(){
  'use strict';
  try {

  // ================= CONFIG & TUNABLES =================
  // Every feel-number is the handoff's tuned value (specs/blocky-striker/handoff/README.md).
  // Config defaults are fixed in v1 (spec Non-Goals): arcade / 60s / 3 lives.
  var CFG = { startSeconds: 60, maxLives: 3, diffMult: 1 };
  var SCORING = ${scoringJson};
  var OBK = ${obstaclesJson};
  var ROSTER = ${rosterJson};
  var BEST_KEY = 'zporti-striker-best';     // spec AC-14 (handoff key)
  var AVATAR_KEY = 'zporti-blocky-avatar';  // spec AC-6 — last-picked avatar survives relaunch

  var LANES = [-4.4, -2.2, 0, 2.2, 4.4];
  var ROW_EVERY = 14;            // world units between spawned rows
  var GOAL_EVERY_ROWS = 12;      // every 12th row is a goal gate
  var PLAYER_CLAMP_X = 5.6;
  var STEER_SPEED = 10;          // playerX units/s while steering
  var DRAG_STEER = 0.022;        // px dragged -> x units
  var SPEED_START = 12, SPEED_ACCEL = 0.14, SPEED_CAP = 30;
  var JUMP_VY = 8.2, GRAVITY = 22;
  var KICK_T = 0.7, KICK_FLY = 34, KICK_ARC = 1.1;
  var BALL_REST_Z = -3.4, BALL_REST_DX = 0.75;
  var INVINCIBLE_S = 1.6;
  // v2 gameplay-depth tunables (spec v2 stories 6-8; runtime is authoritative for these)
  var SLIDE_T = 0.65;            // slide window (s); crouch height 0.7 is baked into the predicate
  var SWIPE_PX = 46;             // vertical swipe threshold (px): up = jump, down = slide
  var RAMP_UP = 7, RAMP_TOP = 14, RAMP_H = 1.5, RAMP_LANE_W = 2.0;
  var RAMP_ROW_MOD = 9, RAMP_ROW_OFFSET = 4; // 12k mod 9 cycles 3/6/0 — never collides with goals
  var HAZARD_AT_M = 80, OVERHEAD_AT_M = 140; // distance gates (AC-23)
  var RUNNER_AT_M = 140, ROLLER_AT_M = 100;  // first moving threats
  // v3 tunables (spec v3 stories 9-19)
  var TRAMP_VY = 13;                          // trampoline launch (apex ≈ 3.8 — clears the big wall)
  var TRAMP_LEAD = 8;                         // pad distance in front of a big wall (same lane)
  var BIGWALL_AT_M = 200, BIGWALL_ROW_MOD = 10, BIGWALL_ROW_OFFSET = 7;
  var RAMP_TIER_AT_M = 250;                   // ×2/×3 platforms unlock here (weights 60/30/10)
  var FLYER_AT_M = 180;
  var POWERUP_AT_M = 60, MAGNET_S = 10, X2_S = 10, MAGNET_RADIUS = 3.5;
  var THEME_EVERY_M = 350;
  var MISSIONS_POOL = ${missionsJson};
  var LEVELS = ${levelsJson};
  var LEVELS_KEY = 'zporti-blocky-levels';
  var MODE_KEY = 'zporti-blocky-mode'; // last-used mode persists; Levels is the default (AC-41)
  var MISSION_KEY = 'zporti-blocky-missions';
  var MUTE_KEY = 'zporti-blocky-muted';
  var TRAIL_KEY = 'zporti-blocky-trail';
  var TRAILS = [
    { id: 'none', name: 'None', color: 0, min: 0 },
    { id: 'gold', name: 'Gold', color: 0xFCD34D, min: 800 },
    { id: 'mint', name: 'Mint', color: 0x6EE7B7, min: 2000 },
    { id: 'violet', name: 'Violet', color: 0xA78BFA, min: 4000 },
  ];
  var HUD_THROTTLE = 0.25;       // never setState-per-frame — HUD writes at most every 250ms
  var HINT_MS = 4000, TOAST_MS = 1600;
  var MAX_DT = 0.05;             // tab-switch clamp: nothing teleports

  // ================= DOM =================
  function el(id){ return document.getElementById(id); }
  var stage = el('stage'), hud = el('hud'), startScreen = el('startScreen'), overScreen = el('overScreen');
  var hudCoins = el('hudCoins'), hudScore = el('hudScore'), hudTime = el('hudTime'),
      hudDist = el('hudDist'), hudHearts = el('hudHearts'), hudAvatar = el('hudAvatar'),
      timerPill = el('timerPill'), hintEl = el('hint'), toastMount = el('toastMount'),
      bestLine = el('bestLine'), hudObjective = el('hudObjective');

  // ================= UI STATE =================
  var screen = 'start'; // 'start' | 'playing' | 'over' | 'revive'
  var score = 0, coinsCt = 0, goals = 0, lives = CFG.maxLives, timeLeft = CFG.startSeconds;
  var best = 0, avatarId = null;
  // v3 UI/meta state
  var paused = false, muted = false, trailId = 'none';
  var combo = 0, comboMult = 1, magnetT = 0, x2T = 0, shieldOn = false;
  var usedRevive = false, reviveIv = null, shakeT = 0;
  var missions = [], missionState = { day: '', progress: {}, done: {} };
  // v4 campaign state: armed mode/level, persisted best stars, per-run stat counters
  var mode = 'level', levelN = 1, levelState = { stars: {} }; // campaign-first default (AC-41)
  try {
    var storedMode = localStorage.getItem(MODE_KEY);
    if (storedMode === 'endless' || storedMode === 'level') mode = storedMode;
  } catch (e) {}
  var runSpeedMult = 1, levelCleared = false;
  var runSmashes = 0, runSlides = 0, runBounces = 0;
  try {
    var rawLevels = JSON.parse(localStorage.getItem(LEVELS_KEY) || 'null');
    if (rawLevels && rawLevels.stars) levelState = rawLevels;
  } catch (e) {}
  while (levelN < LEVELS.length && (levelState.stars[levelN] || 0) >= 1) levelN++; // arm first uncleared
  try { best = parseInt(localStorage.getItem(BEST_KEY) || '0', 10) || 0; } catch (e) {}
  try { avatarId = localStorage.getItem(AVATAR_KEY); } catch (e) {}
  try { muted = localStorage.getItem(MUTE_KEY) === '1'; } catch (e) {}
  try { trailId = localStorage.getItem(TRAIL_KEY) || 'none'; } catch (e) {}
  if (!ROSTER.some(function(r){ return r.id === avatarId; })) avatarId = ROSTER[0].id;

  function currentAvatar(){
    for (var i = 0; i < ROSTER.length; i++) if (ROSTER[i].id === avatarId) return ROSTER[i];
    return ROSTER[0];
  }
  function setScreen(next){
    screen = next;
    startScreen.classList.toggle('hidden', next !== 'start');
    overScreen.classList.toggle('hidden', next !== 'over');
    hud.classList.toggle('hidden', next !== 'playing');
    el('reviveScreen').classList.toggle('hidden', next !== 'revive');
  }
  function fmtTime(s){
    var t = Math.max(0, s), mm = Math.floor(t / 60), ss = Math.floor(t % 60);
    return mm + ':' + String(ss).padStart(2, '0');
  }
  function renderHud(){
    hudCoins.textContent = String(coinsCt);
    hudScore.textContent = String(score);
    hudTime.textContent = fmtTime(timeLeft);
    timerPill.classList.toggle('hidden', mode === 'level'); // no countdown in level mode (AC-42)
    timerPill.classList.toggle('low', mode !== 'level' && timeLeft <= 10);
    hudDist.textContent = Math.floor(distGame) + ' m';
    if (mode === 'level') {
      var lvObj = currentLevel().objective;
      var lvStats = currentRunStats();
      hudObjective.textContent = '\\uD83C\\uDFAF ' + objLabel(lvObj) + ' \\u00b7 ' +
        Math.min(lvStats[lvObj.type] || 0, lvObj.target) + '/' + lvObj.target;
      hudObjective.classList.remove('hidden');
    } else {
      hudObjective.classList.add('hidden');
    }
    var h = '';
    for (var i = 0; i < CFG.maxLives; i++) h += '<span class="' + (i < lives ? '' : 'spent') + '">\\u2665</span>';
    hudHearts.innerHTML = h;
  }
  function renderBestLine(){
    bestLine.classList.toggle('hidden', !(best > 0));
    bestLine.textContent = 'Best: ' + best + ' pts';
  }
  function renderAvatars(){
    var grid = el('avatarGrid');
    grid.innerHTML = '';
    ROSTER.forEach(function(av){
      var cell = document.createElement('div');
      cell.className = 'avatar-cell' + (av.id === avatarId ? ' sel' : '');
      var thumb = document.createElement('div');
      thumb.className = 'avatar-thumb';
      thumb.style.backgroundImage = "url('" + av.img + "')";
      var name = document.createElement('span');
      name.className = 'avatar-name';
      name.textContent = av.name;
      cell.appendChild(thumb); cell.appendChild(name);
      cell.addEventListener('click', function(){
        avatarId = av.id;
        try { localStorage.setItem(AVATAR_KEY, avatarId); } catch (e) {}
        renderAvatars();
        if (playerGroup) buildPlayer();
      });
      grid.appendChild(cell);
    });
    hudAvatar.style.backgroundImage = "url('" + currentAvatar().img + "')";
  }
  var toastTO = null;
  function showToast(text, bg){
    toastMount.innerHTML = '';
    var t = document.createElement('div');
    t.className = 'toast';
    t.style.background = bg;
    t.textContent = text;
    toastMount.appendChild(t);
    clearTimeout(toastTO);
    toastTO = setTimeout(function(){ toastMount.innerHTML = ''; }, TOAST_MS);
  }

  // ================= v3: SCORING MULTIPLIERS, POWER-UPS, MISSIONS, TRAILS =================
  // Combo counts skill events (smash / kicked goal / near-miss) and any hit resets it (AC-31).
  function addPoints(base, comboEligible){
    var gained = base * (comboEligible ? comboMult : 1) * (x2T > 0 ? 2 : 1);
    score += gained;
    return gained;
  }
  function renderCombo(){ el('hudCombo').textContent = comboMult > 1 ? '\\u00d7' + comboMult : ''; }
  function comboEvent(){
    combo += 1;
    comboMult = 1 + Math.min(4, Math.floor(combo / 4));
    renderCombo();
  }
  function comboBreak(){ combo = 0; comboMult = 1; renderCombo(); }
  function renderChips(){
    var s = '';
    if (magnetT > 0) s += '<span class="chip magnet">MAGNET ' + Math.ceil(magnetT) + 's</span>';
    if (shieldOn) s += '<span class="chip shield">SHIELD</span>';
    if (x2T > 0) s += '<span class="chip x2">\\u00d72 ' + Math.ceil(x2T) + 's</span>';
    el('chips').innerHTML = s;
  }
  function buzz(ms){ try { if (navigator.vibrate) navigator.vibrate(ms); } catch (e) {} }

  // Daily missions (AC-32/33): deterministic per UTC day, progress persisted across runs.
  function utcDayKey(){
    var d = new Date();
    return d.getUTCFullYear() + '-' + (d.getUTCMonth() + 1) + '-' + d.getUTCDate();
  }
  function loadMissions(){
    var day = utcDayKey();
    missions = pickDailyMissions(day, MISSIONS_POOL);
    missionState = { day: day, progress: {}, done: {} };
    try {
      var raw = JSON.parse(localStorage.getItem(MISSION_KEY) || 'null');
      if (raw && raw.day === day) missionState = raw;
    } catch (e) {}
    renderMissions();
  }
  function saveMissions(){ try { localStorage.setItem(MISSION_KEY, JSON.stringify(missionState)); } catch (e) {} }
  function renderMissions(){
    var panel = el('missionsPanel');
    panel.innerHTML = '';
    missions.forEach(function(m){
      var row = document.createElement('div');
      var p = Math.min(m.target, missionState.progress[m.id] || 0);
      var done = !!missionState.done[m.id];
      row.className = 'mission' + (done ? ' done' : '');
      var lbl = document.createElement('span');
      lbl.textContent = (done ? '\\u2713 ' : '') + m.label;
      var prog = document.createElement('span');
      prog.className = 'prog';
      prog.textContent = p + '/' + m.target;
      row.appendChild(lbl); row.appendChild(prog);
      panel.appendChild(row);
    });
  }
  function missionEvent(id, amount){
    if (screen !== 'playing') return;
    var m = null;
    for (var i = 0; i < missions.length; i++) if (missions[i].id === id) m = missions[i];
    if (!m || missionState.done[m.id]) return;
    if (id === 'distance') missionState.progress[id] = Math.max(missionState.progress[id] || 0, amount);
    else missionState.progress[id] = (missionState.progress[id] || 0) + amount;
    if (missionState.progress[m.id] >= m.target) {
      missionState.done[m.id] = true;
      addPoints(SCORING.MISSION_BONUS_POINTS, false);
      showToast('MISSION COMPLETE! +' + SCORING.MISSION_BONUS_POINTS, '#6EE7B7');
      sfx('goal');
    }
    saveMissions();
  }

  // Trails (AC-39): cosmetic wakes unlocked by best-points milestones, persisted locally.
  function currentTrail(){
    for (var i = 0; i < TRAILS.length; i++) if (TRAILS[i].id === trailId) return TRAILS[i];
    return TRAILS[0];
  }
  function renderTrails(){
    var row = el('trailRow');
    row.innerHTML = '';
    TRAILS.forEach(function(tr){
      var c = document.createElement('div');
      var unlocked = best >= tr.min;
      c.className = 'trail' + (tr.id === trailId ? ' sel' : '') + (unlocked ? '' : ' locked');
      if (tr.color) c.style.background = '#' + ('000000' + tr.color.toString(16)).slice(-6);
      c.textContent = unlocked ? (tr.color ? '' : '\\u2014') : String(tr.min);
      c.title = tr.name;
      if (unlocked) c.addEventListener('click', function(){
        trailId = tr.id;
        try { localStorage.setItem(TRAIL_KEY, trailId); } catch (e) {}
        renderTrails();
      });
      row.appendChild(c);
    });
  }
  if (best < currentTrail().min) trailId = 'none'; // stored pick must still be unlocked

  // ================= v4: LEVEL CAMPAIGN (AC-41..44) =================
  function currentLevel(){ return LEVELS[levelN - 1] || LEVELS[0]; }
  function currentRunStats(){
    return { distance: Math.floor(distGame), coins: coinsCt, goals: goals,
             smashes: runSmashes, slides: runSlides, bounces: runBounces };
  }
  function saveLevels(){ try { localStorage.setItem(LEVELS_KEY, JSON.stringify(levelState)); } catch (e) {} }
  function objLabel(o){
    if (o.type === 'distance') return 'Reach ' + o.target + ' m';
    if (o.type === 'coins') return 'Collect ' + o.target + ' coins';
    if (o.type === 'goals') return 'Score ' + o.target + ' kicked goal' + (o.target > 1 ? 's' : '');
    if (o.type === 'smashes') return 'Smash ' + o.target + ' obstacles';
    if (o.type === 'slides') return 'Slide under ' + o.target;
    return 'Bounce ' + o.target + ' pads';
  }
  function starGlyphs(n){ return Array(n + 1).join('\\u2605') + Array(3 - n + 1).join('\\u2606'); }
  function renderLevelGrid(){
    var grid = el('levelGrid');
    grid.innerHTML = '';
    LEVELS.forEach(function(lv){
      var cell = document.createElement('div');
      var unlocked = isLevelUnlocked(lv.n, levelState.stars);
      var st = levelState.stars[lv.n] || 0;
      cell.className = 'level-cell' + (lv.n === levelN ? ' sel' : '') + (unlocked ? '' : ' locked');
      var num = document.createElement('span');
      num.className = 'level-num';
      num.textContent = unlocked ? String(lv.n) : '\\uD83D\\uDD12';
      var stars = document.createElement('span');
      stars.className = 'level-stars';
      stars.textContent = st > 0 ? Array(st + 1).join('\\u2605') : '';
      cell.appendChild(num); cell.appendChild(stars);
      if (unlocked) cell.addEventListener('click', function(){ levelN = lv.n; renderLevelGrid(); });
      grid.appendChild(cell);
    });
    el('levelObjLine').textContent = 'Level ' + levelN + ': ' + objLabel(currentLevel().objective);
  }
  function setMode(m){
    mode = m;
    try { localStorage.setItem(MODE_KEY, m); } catch (e) {}
    el('modeEndless').classList.toggle('sel', m === 'endless');
    el('modeLevels').classList.toggle('sel', m === 'level');
    el('levelGrid').classList.toggle('hidden', m !== 'level');
    el('levelObjLine').classList.toggle('hidden', m !== 'level');
    if (m === 'level') renderLevelGrid();
  }

  function renderSoundBtn(){ el('btnSound').innerHTML = muted ? '&#128263;' : '&#128266;'; }
  function setPaused(p){
    if (p && screen !== 'playing') return;
    paused = p;
    el('pauseScreen').classList.toggle('hidden', !paused);
  }

  // ================= AUDIO (procedural, lazy, never blocking) =================
  var actx = null;
  function beep(freq, dur, type, vol){
    if (muted) return;
    try {
      actx = actx || new (window.AudioContext || window.webkitAudioContext)();
      var o = actx.createOscillator(), gn = actx.createGain();
      o.type = type || 'square'; o.frequency.value = freq;
      gn.gain.setValueAtTime(vol || 0.08, actx.currentTime);
      gn.gain.exponentialRampToValueAtTime(0.001, actx.currentTime + dur);
      o.connect(gn); gn.connect(actx.destination);
      o.start(); o.stop(actx.currentTime + dur);
    } catch (e) { /* audio unavailable */ }
  }
  function sfx(name){
    if (name === 'coin') { beep(980, 0.09); beep(1320, 0.12); }
    if (name === 'goal') { beep(523, 0.12); setTimeout(function(){ beep(659, 0.12); }, 90); setTimeout(function(){ beep(784, 0.22); }, 180); }
    if (name === 'hit') beep(140, 0.25, 'sawtooth', 0.12);
    if (name === 'life') { beep(660, 0.1, 'sine'); beep(880, 0.18, 'sine'); }
    if (name === 'over') { beep(330, 0.2, 'sawtooth'); setTimeout(function(){ beep(220, 0.4, 'sawtooth'); }, 180); }
  }
  // v3 music: a light step-sequenced bass loop — alive only mid-run, silenced by the mute toggle.
  var musicStep = 0;
  var MUSIC_PATTERN = [110, 0, 220, 0, 146.83, 0, 220, 0, 98, 0, 196, 0, 130.81, 0, 196, 0];
  setInterval(function(){
    if (muted || paused || screen !== 'playing') return;
    var f = MUSIC_PATTERN[musicStep % MUSIC_PATTERN.length];
    musicStep++;
    if (f) beep(f, 0.16, 'triangle', 0.03);
  }, 220);

  // ================= THREE SCENE =================
  // Legacy (r128-era) color pipeline the handoff was tuned on: no color management, linear output.
  THREE.ColorManagement.enabled = false;

  var scene = new THREE.Scene();
  scene.background = new THREE.Color(0x171029);
  scene.fog = new THREE.Fog(0x171029, 60, 150);
  var camera = new THREE.PerspectiveCamera(58, 1, 0.1, 300);
  camera.position.set(0, 5.4, 7.8);
  camera.lookAt(0, 0.7, -8);
  var renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  stage.appendChild(renderer.domElement);
  function sizeToStage(){
    var w = stage.clientWidth || window.innerWidth, h = stage.clientHeight || window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }
  sizeToStage();
  window.addEventListener('resize', sizeToStage); // portrait AND landscape — no letterboxing

  // r155+ removed legacy lighting (intensities became physical units); ×π restores the exact
  // r128-era brightness the handoff's 0.55 / 1.05 / 0.3 values were tuned on.
  var LEGACY_LIGHT_SCALE = Math.PI;
  scene.add(new THREE.AmbientLight(0x8877cc, 0.55 * LEGACY_LIGHT_SCALE));
  var sun = new THREE.DirectionalLight(0xfff2d8, 1.05 * LEGACY_LIGHT_SCALE);
  sun.position.set(10, 25, 8); scene.add(sun);
  var fill = new THREE.DirectionalLight(0xa78bfa, 0.3 * LEGACY_LIGHT_SCALE);
  fill.position.set(-12, 10, -10); scene.add(fill);

  // v3 themes (AC-37): twilight → sunset → night → frost; colors blend continuously in tick().
  var THEME_DEFS = [
    { bg: 0x171029, sun: 0xfff2d8 },
    { bg: 0x2b1230, sun: 0xffc37a },
    { bg: 0x0a0a18, sun: 0xcdd6ff },
    { bg: 0x101c26, sun: 0xbfe3ff },
  ];
  var THEME_BG_C = [], THEME_SUN_C = [];
  for (var thi = 0; thi < THEME_DEFS.length; thi++) {
    THEME_BG_C.push(new THREE.Color(THEME_DEFS[thi].bg));
    THEME_SUN_C.push(new THREE.Color(THEME_DEFS[thi].sun));
  }

  // ---- procedural canvas textures (NearestFilter = the blocky voxel look; never smooth) ----
  function px(size, fn){
    var c = document.createElement('canvas'); c.width = size; c.height = size;
    var g = c.getContext('2d'); fn(g, size);
    var t = new THREE.CanvasTexture(c);
    t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter;
    return t;
  }
  function noiseFill(g, size, colors, cell){
    for (var y = 0; y < size; y += cell) for (var x = 0; x < size; x += cell) {
      g.fillStyle = colors[Math.floor(Math.random() * colors.length)];
      g.fillRect(x, y, cell, cell);
    }
  }
  function makeGrass(){
    return px(128, function(g, s){
      var bands = [['#3aa049', '#379a46'], ['#2f8d3e', '#2c883b']];
      for (var row = 0; row < 2; row++) {
        var base = bands[row][0], alt = bands[row][1];
        g.fillStyle = base; g.fillRect(0, row * 64, s, 64);
        g.fillStyle = alt;
        for (var y = row * 64; y < row * 64 + 64; y += 8) for (var x = 0; x < s; x += 8) {
          if ((x + y) % 16 === 0) g.fillRect(x, y, 8, 8);
        }
      }
    });
  }
  function makeBanner(){
    return px(256, function(g, s){
      g.fillStyle = '#0F0A1A'; g.fillRect(0, 0, s, s);
      g.fillStyle = '#A78BFA'; g.font = '900 64px Inter, sans-serif';
      g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText('ZPORTI', s / 2, s / 2);
      g.fillStyle = '#FCD34D'; g.fillRect(0, s - 14, s, 14); g.fillRect(0, 0, s, 14);
    });
  }
  function makeBallTex(){
    return px(64, function(g, s){
      g.fillStyle = '#f4f4f4'; g.fillRect(0, 0, s, s);
      g.fillStyle = '#1a1a1a';
      [[8, 8], [40, 8], [24, 28], [8, 48], [40, 48]].forEach(function(p){ g.fillRect(p[0], p[1], 14, 12); });
    });
  }
  function makeBallHeadTex(base, patch){
    return px(64, function(g, s){
      g.fillStyle = base; g.fillRect(0, 0, s, s);
      g.fillStyle = patch;
      [[6, 6], [38, 4], [22, 24], [4, 44], [40, 42], [24, 52]].forEach(function(p){ g.fillRect(p[0], p[1], 15, 13); });
    });
  }
  function spriteTex(draw){
    return px(16, function(g){ g.clearRect(0, 0, 16, 16); draw(g); });
  }

  // ---- static world: pitch, touchlines, ad boards, stands, floodlights ----
  var grassTex = makeGrass();
  grassTex.wrapS = grassTex.wrapT = THREE.RepeatWrapping;
  grassTex.repeat.set(4, 60);
  var ground = new THREE.Mesh(new THREE.PlaneGeometry(32, 480), new THREE.MeshLambertMaterial({ map: grassTex }));
  ground.rotation.x = -Math.PI / 2; ground.position.z = -180;
  scene.add(ground);

  var lineMat = new THREE.MeshBasicMaterial({ color: 0xe8e8e8 });
  [-6.6, 6.6].forEach(function(x){
    var line = new THREE.Mesh(new THREE.PlaneGeometry(0.18, 480), lineMat);
    line.rotation.x = -Math.PI / 2; line.position.set(x, 0.01, -180);
    scene.add(line);
  });

  // Trackside LED ad-board walls: segmented panels along both touchlines cycle through a set of
  // designs (big ZPORTI wordmark among them) every few seconds while the run scrolls past — like
  // rotating pitch-side adverts. A clean dark stand rises above so the lit panels pop.
  function panelTex(draw){
    var c = document.createElement('canvas'); c.width = 512; c.height = 96;
    var g = c.getContext('2d');
    g.fillStyle = '#0F0A1A'; g.fillRect(0, 0, 512, 96);
    draw(g);
    var t = new THREE.CanvasTexture(c);
    t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter;
    return t;
  }
  function centerText(g, text, color, size){
    g.textAlign = 'center'; g.textBaseline = 'middle';
    g.font = '900 ' + size + 'px Inter, sans-serif';
    g.fillStyle = color;
    g.fillText(text, 256, 50);
  }
  var PANEL_DESIGNS = [
    // 1. ZPORTI hero — the brand, big and clean
    new THREE.MeshBasicMaterial({ map: panelTex(function(g){
      g.fillStyle = '#FCD34D'; g.fillRect(0, 0, 512, 6); g.fillRect(0, 90, 512, 6);
      centerText(g, 'Z P O R T I', '#A78BFA', 58);
    }) }),
    // 2. BLOCKY STRIKER on violet
    new THREE.MeshBasicMaterial({ map: panelTex(function(g){
      g.fillStyle = '#2b1f49'; g.fillRect(0, 0, 512, 96);
      centerText(g, 'BLOCKY STRIKER', '#FCD34D', 44);
    }) }),
    // 3. ZPORTI inverted — violet field, dark wordmark
    new THREE.MeshBasicMaterial({ map: panelTex(function(g){
      g.fillStyle = '#A78BFA'; g.fillRect(0, 0, 512, 96);
      centerText(g, 'ZPORTI', '#0F0A1A', 60);
    }) }),
    // 4. GOAL chant, mint on dark with ball marks
    new THREE.MeshBasicMaterial({ map: panelTex(function(g){
      centerText(g, '\\u26BD GOAL! GOAL! \\u26BD', '#6EE7B7', 40);
    }) }),
    // 5. Diagonal stripes divider (no text — visual breathing room)
    new THREE.MeshBasicMaterial({ map: panelTex(function(g){
      g.fillStyle = '#38285c';
      for (var sx = -96; sx < 512; sx += 48) {
        g.beginPath(); g.moveTo(sx, 96); g.lineTo(sx + 48, 0); g.lineTo(sx + 72, 0); g.lineTo(sx + 24, 96); g.closePath(); g.fill();
      }
    }) }),
  ];
  var adPanels = [];
  var PANEL_LEN = 40, PANEL_COUNT = 12, PANEL_H = 2.6;
  [-8.2, 8.2].forEach(function(x, sideIdx){
    var inner = x < 0 ? 1 : -1; // toward the pitch
    var backing = new THREE.Mesh(new THREE.BoxGeometry(0.5, PANEL_H + 0.4, 480), new THREE.MeshLambertMaterial({ color: 0x111827 }));
    backing.position.set(x, (PANEL_H + 0.4) / 2, -180); scene.add(backing);
    for (var pi2 = 0; pi2 < PANEL_COUNT; pi2++) {
      var panel = new THREE.Mesh(new THREE.PlaneGeometry(PANEL_LEN - 0.8, PANEL_H), PANEL_DESIGNS[0]);
      panel.rotation.y = inner * Math.PI / 2;
      panel.position.set(x + inner * 0.26, 0.3 + PANEL_H / 2, 60 - PANEL_LEN / 2 - pi2 * PANEL_LEN);
      scene.add(panel);
      adPanels.push({ mesh: panel, slot: pi2 + sideIdx * 2 }); // side offset staggers the designs
    }
    var ledge = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.12, 480), new THREE.MeshBasicMaterial({ color: 0xFCD34D }));
    ledge.position.set(x, PANEL_H + 0.46, -180); scene.add(ledge);
    var stand = new THREE.Mesh(new THREE.BoxGeometry(6, 6, 480), new THREE.MeshLambertMaterial({ color: 0x1d1535 }));
    stand.position.set(x - inner * 3.3, PANEL_H + 3.4, -180); scene.add(stand);
  });
  // The boards scroll WITH the world (same dz as the obstacles) and wrap to the back of the
  // track once they pass the camera — each re-entering board gets a fresh design, like rotating
  // pitch-side adverts you keep running past.
  function scrollAdPanels(dz){
    for (var i = 0; i < adPanels.length; i++) {
      var ap = adPanels[i];
      ap.mesh.position.z += dz;
      if (ap.mesh.position.z - PANEL_LEN / 2 > 60) {
        ap.mesh.position.z -= PANEL_COUNT * PANEL_LEN;
        ap.slot += PANEL_COUNT; // 12 ≡ 2 (mod 5 designs) → the fresh board differs from its old self and its neighbors
        ap.mesh.material = PANEL_DESIGNS[ap.slot % PANEL_DESIGNS.length];
      }
    }
  }
  adPanels.forEach(function(ap){ ap.mesh.material = PANEL_DESIGNS[ap.slot % PANEL_DESIGNS.length]; });

  [[-8, -60], [8, -60], [-8, -140], [8, -140]].forEach(function(p){
    var pole = new THREE.Mesh(new THREE.BoxGeometry(0.4, 14, 0.4), new THREE.MeshLambertMaterial({ color: 0x374151 }));
    pole.position.set(p[0] * 1.3, 7, p[1]); scene.add(pole);
    var lamp = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1, 0.5), new THREE.MeshBasicMaterial({ color: 0xfff7cc }));
    lamp.position.set(p[0] * 1.3, 13.6, p[1]); scene.add(lamp);
  });

  // ---- obstacle / pickup / gate materials ----
  var matCache = {
    wall: new THREE.MeshLambertMaterial({ map: px(64, function(g, s){ noiseFill(g, s, ['#6B7280', '#7b8494', '#5d6572', '#828b9c'], 8); }) }),
    crate: new THREE.MeshLambertMaterial({ map: px(64, function(g, s){ noiseFill(g, s, ['#8b5a2b', '#9a6a38', '#7c4f24'], 8); g.fillStyle = '#5e3c1c'; g.fillRect(0, 0, s, 6); g.fillRect(0, s - 6, s, 6); g.fillRect(0, 0, 6, s); g.fillRect(s - 6, 0, 6, s); }) }),
    hedge: new THREE.MeshLambertMaterial({ map: px(64, function(g, s){ noiseFill(g, s, ['#14532d', '#166534', '#0f4a26', '#1a6b38'], 8); }) }),
    mover: new THREE.MeshLambertMaterial({ map: px(64, function(g, s){ noiseFill(g, s, ['#6B46C1', '#7c53d6', '#5d3ba8'], 8); g.fillStyle = '#FCD34D'; g.fillRect(0, 26, s, 12); }) }),
    post: new THREE.MeshLambertMaterial({ color: 0xf4f4f4 }),
    net: new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.18, side: THREE.DoubleSide }),
    // v2 hazards
    fence: new THREE.MeshLambertMaterial({ map: px(64, function(g, s){ g.fillStyle = '#e5e7eb'; g.fillRect(0, 0, s, s); g.fillStyle = '#9ca3af'; for (var fx = 12; fx < s; fx += 16) g.fillRect(fx, 0, 4, s); g.fillStyle = '#d1d5db'; g.fillRect(0, 8, s, 6); g.fillRect(0, s - 14, s, 6); }) }),
    metal: new THREE.MeshLambertMaterial({ color: 0x9CA3AF }),
    coal: new THREE.MeshLambertMaterial({ map: px(64, function(g, s){ noiseFill(g, s, ['#1f2937', '#111827', '#374151'], 8); }) }),
    flame: new THREE.MeshBasicMaterial({ transparent: true, side: THREE.DoubleSide, depthWrite: false, map: px(64, function(g, s){ g.clearRect(0, 0, s, s); var cols = ['#F97316', '#FB923C', '#FBBF24', '#FDE68A']; for (var fy = 0; fy < s; fy += 8) for (var fx2 = 0; fx2 < s; fx2 += 8) { if (Math.random() < 0.15 + (fy / s) * 0.8) { g.fillStyle = cols[Math.floor(Math.random() * cols.length)]; g.fillRect(fx2, fy, 8, 8); } } }) }),
    // v3
    cone: new THREE.MeshLambertMaterial({ color: 0xF97316 }),
    trampTop: new THREE.MeshLambertMaterial({ color: 0x6EE7B7 }),
  };
  var coinTex = px(32, function(g, s){ g.fillStyle = '#FCD34D'; g.fillRect(2, 2, s - 4, s - 4); g.fillStyle = '#F59E0B'; g.fillRect(8, 8, s - 16, s - 16); g.fillStyle = '#FDE68A'; g.fillRect(4, 4, 6, 6); });
  function iconTex(draw){
    return px(32, function(g, s){ g.clearRect(0, 0, s, s); draw(g, s); });
  }
  var magnetTex = iconTex(function(g){ g.fillStyle = '#F87171'; g.fillRect(6, 4, 6, 16); g.fillRect(20, 4, 6, 16); g.fillRect(6, 16, 20, 8); g.fillStyle = '#FFFFFF'; g.fillRect(6, 4, 6, 5); g.fillRect(20, 4, 6, 5); });
  var shieldTex = iconTex(function(g){ g.fillStyle = '#60A5FA'; g.fillRect(6, 4, 20, 14); g.fillRect(8, 18, 16, 4); g.fillRect(12, 22, 8, 4); g.fillStyle = '#DBEAFE'; g.fillRect(10, 8, 12, 6); });
  var x2Tex = iconTex(function(g, s){ g.fillStyle = '#FCD34D'; g.font = '900 18px sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('\\u00d72', s / 2, s / 2 + 1); });
  var heartTex = spriteTex(function(g){ g.fillStyle = '#F87171'; [[3, 3, 4, 3], [9, 3, 4, 3], [2, 5, 12, 4], [4, 9, 8, 2], [6, 11, 4, 2]].forEach(function(r){ g.fillRect(r[0], r[1], r[2], r[3]); }); });
  var clockTex = spriteTex(function(g){ g.fillStyle = '#6EE7B7'; g.fillRect(3, 2, 10, 12); g.fillStyle = '#0F0A1A'; g.fillRect(5, 4, 6, 8); g.fillStyle = '#FFFFFF'; g.fillRect(7, 5, 2, 4); g.fillRect(8, 8, 3, 2); });

  // ================= PLAYER (voxel Zporti mascot) & BALL =================
  var playerGroup = null, playerShadow = null, legL, legR, armL, armR;
  function buildPlayer(){
    if (playerGroup) scene.remove(playerGroup);
    var pal = currentAvatar().pal;
    var jersey = new THREE.MeshLambertMaterial({ color: new THREE.Color(pal.jersey) });
    var trim = new THREE.MeshLambertMaterial({ color: new THREE.Color(pal.trim) });
    var skin = new THREE.MeshLambertMaterial({ color: 0xF0A868 });
    var boot = new THREE.MeshLambertMaterial({ color: 0xf4f4f4 });
    var hairMat = new THREE.MeshLambertMaterial({ color: new THREE.Color(pal.hair) });
    var g = new THREE.Group();
    // soccer-ball head — the Zporti mascot look
    var head = new THREE.Mesh(new THREE.SphereGeometry(0.62, 10, 8), new THREE.MeshLambertMaterial({ map: makeBallHeadTex(pal.ball, pal.patch) }));
    head.position.y = 1.78; g.add(head);
    var hair1 = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.2, 0.5), hairMat); hair1.position.set(0, 2.38, 0.02); hair1.rotation.z = 0.12; g.add(hair1);
    var hair2 = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.22, 0.34), hairMat); hair2.position.set(0.26, 2.5, 0); hair2.rotation.z = -0.4; g.add(hair2);
    var torso = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.5, 0.36), jersey); torso.position.y = 0.98; g.add(torso);
    var belt = new THREE.Mesh(new THREE.BoxGeometry(0.64, 0.1, 0.38), trim); belt.position.y = 0.76; g.add(belt);
    armL = new THREE.Group();
    var armMeshL = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.5, 0.16), skin); armMeshL.position.y = -0.22; armL.add(armMeshL);
    armL.position.set(-0.42, 1.18, 0); armL.rotation.z = 0.18; g.add(armL);
    armR = new THREE.Group();
    armR.add(armMeshL.clone());
    armR.position.set(0.42, 1.18, 0); armR.rotation.z = -0.18; g.add(armR);
    function makeLeg(x){
      var hip = new THREE.Group(); hip.position.set(x, 0.78, 0);
      var thigh = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.34, 0.2), jersey); thigh.position.y = -0.14; hip.add(thigh);
      var knee = new THREE.Group(); knee.position.y = -0.32; hip.add(knee);
      var calf = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.3, 0.17), trim); calf.position.y = -0.14; knee.add(calf);
      var foot = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.12, 0.3), boot); foot.position.set(0, -0.32, 0.05); knee.add(foot);
      hip.userData.knee = knee;
      return hip;
    }
    legL = makeLeg(-0.18); g.add(legL);
    legR = makeLeg(0.18); g.add(legR);
    var shadow = new THREE.Mesh(new THREE.CircleGeometry(0.55, 16), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.3 }));
    shadow.rotation.x = -Math.PI / 2; shadow.position.y = 0.02; g.add(shadow);
    playerShadow = shadow;
    g.rotation.y = Math.PI; // face down the field, back to camera
    playerGroup = g; scene.add(g);
  }
  var ball = new THREE.Mesh(new THREE.SphereGeometry(0.42, 8, 6), new THREE.MeshLambertMaterial({ map: makeBallTex() }));
  ball.position.set(BALL_REST_DX, 0.42, BALL_REST_Z);
  scene.add(ball);
  buildPlayer();

  // ================= v3: PARTICLES, TRAIL WAKE & SCREEN SHAKE (AC-38/39) =================
  var reduceMotion = false;
  try { reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
  var PARTICLES = [], pIdx = 0, trailAcc = 0;
  for (var pi = 0; pi < 30; pi++) {
    var pm = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.14, 0.14), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true }));
    pm.visible = false;
    scene.add(pm);
    PARTICLES.push({ mesh: pm, vx: 0, vy: 0, vz: 0, ttl: 0, max: 0.6, grav: true });
  }
  function burst(x, y, z, color, n){
    if (reduceMotion) return;
    for (var i = 0; i < n; i++) {
      var p = PARTICLES[pIdx = (pIdx + 1) % PARTICLES.length];
      p.mesh.visible = true;
      p.mesh.material.color.setHex(color);
      p.mesh.material.opacity = 1;
      p.mesh.position.set(x, y, z);
      p.vx = (Math.random() - 0.5) * 6; p.vy = 2 + Math.random() * 5; p.vz = (Math.random() - 0.5) * 6;
      p.ttl = p.max = 0.5 + Math.random() * 0.3;
      p.grav = true;
    }
  }
  function updateParticles(dt){
    for (var i = 0; i < PARTICLES.length; i++) {
      var p = PARTICLES[i];
      if (p.ttl <= 0) { p.mesh.visible = false; continue; }
      p.ttl -= dt;
      if (p.grav) p.vy -= 18 * dt;
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.y += p.vy * dt;
      p.mesh.position.z += p.vz * dt;
      p.mesh.material.opacity = Math.max(0, p.ttl / p.max);
      if (p.ttl <= 0) p.mesh.visible = false;
    }
  }
  function updateTrail(dt){
    var tr = currentTrail();
    if (!tr.color || reduceMotion || screen !== 'playing' || paused) return;
    trailAcc += dt;
    if (trailAcc < 0.06) return;
    trailAcc = 0;
    var p = PARTICLES[pIdx = (pIdx + 1) % PARTICLES.length];
    p.mesh.visible = true;
    p.mesh.material.color.setHex(tr.color);
    p.mesh.material.opacity = 0.8;
    p.mesh.position.set(playerGroup.position.x + (Math.random() - 0.5) * 0.3, py + 0.15, 0.6);
    p.vx = 0; p.vy = 0.4; p.vz = 2.5;
    p.grav = false;
    p.ttl = p.max = 0.4;
  }

  // ================= SIM STATE (refs only — never per-frame DOM writes) =================
  var playerX = 0, steer = 0, vy = 0, py = 0, kickT = 0, speed = SPEED_START * CFG.diffMult;
  var distGame = 0, spawnZ = 0, rowCount = 0, nextDistScore = SCORING.DIST_STEP_M;
  var invincible = 0, hudTimer = 0;
  var obstacles = [], pickups = [], goalGates = [];
  // v2: slide window, ramp support under the player (py is ABSOLUTE height), moving-threat cadence
  var slideT = 0, support = 0, ramps = [], rampReserve = null;
  var nextRunnerM = RUNNER_AT_M, nextRollerM = ROLLER_AT_M, nextFlyerM = FLYER_AT_M;

  function resetRun(){
    obstacles.concat(pickups).concat(goalGates).concat(ramps).forEach(function(o){ scene.remove(o.mesh); });
    obstacles = []; pickups = []; goalGates = []; ramps = [];
    playerX = 0; steer = 0; vy = 0; py = 0; kickT = 0; speed = SPEED_START * CFG.diffMult * runSpeedMult;
    distGame = 0; spawnZ = 0; rowCount = 0; nextDistScore = SCORING.DIST_STEP_M;
    invincible = 0; hudTimer = 0;
    slideT = 0; support = 0; rampReserve = null;
    nextRunnerM = RUNNER_AT_M; nextRollerM = ROLLER_AT_M; nextFlyerM = FLYER_AT_M;
    combo = 0; comboMult = 1; magnetT = 0; x2T = 0; shieldOn = false; shakeT = 0;
    usedRevive = false; clearInterval(reviveIv); reviveIv = null;
    renderCombo(); renderChips();
    for (var z = -34; z > -160; z -= ROW_EVERY) spawnRow(z);
  }

  // ================= SPAWNERS (handoff §Obstacles, pickups, goals) =================
  // Weighted kind pick, distance-gated (AC-23): the opening of a run reads like v1.
  function pickKind(){
    var pool = [['wall', 15], ['hedge', 13], ['crate', 12], ['mover', 8], ['fence', 11], ['cone', 9]];
    if (distGame >= 40) pool.push(['barrel', 9]);
    if (distGame >= HAZARD_AT_M) { pool.push(['spikes', 8]); pool.push(['fire', 8]); }
    if (distGame >= OVERHEAD_AT_M) { pool.push(['bar', 8]); pool.push(['float', 8]); }
    var total = 0, i;
    for (i = 0; i < pool.length; i++) total += pool[i][1];
    var r = Math.random() * total;
    for (i = 0; i < pool.length; i++) { r -= pool[i][1]; if (r <= 0) return pool[i][0]; }
    return 'wall';
  }
  // One builder per traits-table kind (OBK). Returns the obstacles[] record.
  function buildObstacle(kind, x, z){
    var tr = OBK[kind], mesh, si;
    if (kind === 'bar') {
      mesh = new THREE.Group();
      [-1, 1].forEach(function(s){
        var post = new THREE.Mesh(new THREE.BoxGeometry(0.16, tr.gapY + tr.h, 0.16), matCache.post);
        post.position.set(s * tr.w / 2, (tr.gapY + tr.h) / 2, 0); mesh.add(post);
      });
      var beam = new THREE.Mesh(new THREE.BoxGeometry(tr.w + 0.16, tr.h, 0.3), matCache.wall);
      beam.position.set(0, tr.gapY + tr.h / 2, 0); mesh.add(beam);
      mesh.position.set(x, 0, z);
    } else if (kind === 'float') {
      mesh = new THREE.Mesh(new THREE.BoxGeometry(tr.w, tr.h, 1.2), matCache.crate);
      mesh.position.set(x, tr.gapY + tr.h / 2, z);
    } else if (kind === 'spikes') {
      mesh = new THREE.Group();
      var base = new THREE.Mesh(new THREE.BoxGeometry(tr.w, 0.15, 0.8), matCache.metal);
      base.position.y = 0.075; mesh.add(base);
      for (si = 0; si < 4; si++) {
        var spike = new THREE.Mesh(new THREE.CylinderGeometry(0, 0.16, tr.h - 0.15, 6), matCache.metal);
        spike.position.set(-tr.w / 2 + 0.3 + si * (tr.w - 0.6) / 3, 0.15 + (tr.h - 0.15) / 2, 0);
        mesh.add(spike);
      }
      mesh.position.set(x, 0, z);
    } else if (kind === 'fire') {
      mesh = new THREE.Group();
      var coals = new THREE.Mesh(new THREE.BoxGeometry(tr.w, 0.25, 0.9), matCache.coal);
      coals.position.y = 0.125; mesh.add(coals);
      var flames = [];
      for (si = 0; si < 2; si++) {
        var f = new THREE.Mesh(new THREE.PlaneGeometry(tr.w, tr.h), matCache.flame);
        f.position.y = 0.25 + tr.h / 2;
        f.rotation.y = si * Math.PI / 2;
        mesh.add(f); flames.push(f);
      }
      mesh.userData.flames = flames;
      mesh.position.set(x, 0, z);
    } else if (kind === 'fence') {
      mesh = new THREE.Mesh(new THREE.BoxGeometry(tr.w, tr.h, 0.25), matCache.fence);
      mesh.position.set(x, tr.h / 2, z);
    } else if (kind === 'barrel') {
      mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, tr.h, 10), matCache.crate);
      mesh.position.set(x, tr.h / 2, z);
    } else if (kind === 'cone') {
      mesh = new THREE.Group();
      var cbase = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.08, 0.6), matCache.cone);
      cbase.position.y = 0.04; mesh.add(cbase);
      var ctop = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.34, tr.h - 0.08, 8), matCache.cone);
      ctop.position.y = 0.08 + (tr.h - 0.08) / 2; mesh.add(ctop);
      mesh.position.set(x, 0, z);
    } else if (kind === 'bigwall') {
      mesh = new THREE.Mesh(new THREE.BoxGeometry(tr.w, tr.h, 1), matCache.wall);
      mesh.position.set(x, tr.h / 2, z);
    } else if (kind === 'tramp') {
      mesh = new THREE.Group();
      var tframe = new THREE.Mesh(new THREE.BoxGeometry(tr.w, 0.18, 1.0), matCache.coal);
      tframe.position.y = 0.09; mesh.add(tframe);
      var ttop = new THREE.Mesh(new THREE.BoxGeometry(tr.w - 0.2, 0.12, 0.8), matCache.trampTop);
      ttop.position.y = 0.24; mesh.add(ttop);
      mesh.position.set(x, 0, z);
    } else {
      mesh = new THREE.Mesh(new THREE.BoxGeometry(tr.w, tr.h, kind === 'crate' ? 1.2 : 1), matCache[kind]);
      mesh.position.set(x, tr.h / 2, z);
    }
    scene.add(mesh);
    return { mesh: mesh, kind: kind, w: tr.w, h: tr.h, gap: tr.gapY, smashable: tr.smashable,
             vz: tr.vz || 0, bounce: !!tr.bounce, moving: kind === 'mover',
             phase: Math.random() * 6.28, baseX: x };
  }
  // Moving threats (AC-22) spawn on a distance cadence, not as row members.
  function buildRunner(){
    var x = LANES[Math.floor(Math.random() * 5)];
    var tr = OBK.runner;
    var jersey = new THREE.MeshLambertMaterial({ color: 0xDC2626 });
    var dark = new THREE.MeshLambertMaterial({ color: 0x111827 });
    var skin = new THREE.MeshLambertMaterial({ color: 0xE8B080 });
    var g = new THREE.Group();
    var head = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 0.5), skin); head.position.y = 1.62; g.add(head);
    var hair = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.16, 0.54), dark); hair.position.y = 1.92; g.add(hair);
    var torso = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.55, 0.34), jersey); torso.position.y = 1.08; g.add(torso);
    var legs = [];
    [-0.16, 0.16].forEach(function(lx){
      var leg = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.8, 0.18), dark);
      leg.position.set(lx, 0.4, 0); g.add(leg); legs.push(leg);
    });
    g.position.set(x, 0, -160);
    scene.add(g);
    return { mesh: g, kind: 'runner', w: tr.w, h: tr.h, gap: 0, smashable: tr.smashable, vz: tr.vz,
             moving: false, phase: Math.random() * 6.28, baseX: x, legs: legs };
  }
  function buildRoller(){
    var x = LANES[Math.floor(Math.random() * 5)];
    var tr = OBK.roller;
    var m = new THREE.Mesh(new THREE.SphereGeometry(0.4, 8, 6), new THREE.MeshLambertMaterial({ map: makeBallTex() }));
    m.position.set(x, 0.4, -160);
    scene.add(m);
    return { mesh: m, kind: 'roller', w: tr.w, h: tr.h, gap: 0, smashable: tr.smashable, vz: tr.vz,
             moving: false, phase: 0, baseX: x };
  }
  // Flyers (AC-28): airborne balls of varying size/altitude — always slideable underneath.
  function buildFlyer(){
    var x = LANES[Math.floor(Math.random() * 5)];
    var r = 0.3 + Math.random() * 0.2;
    var gapUnder = 0.75 + Math.random() * 0.65;
    var m = new THREE.Mesh(new THREE.SphereGeometry(r, 8, 6), new THREE.MeshLambertMaterial({ map: makeBallTex() }));
    m.position.set(x, gapUnder + r, -160);
    scene.add(m);
    return { mesh: m, kind: 'flyer', w: r * 2, h: r * 2, gap: gapUnder, smashable: true,
             vz: 8 + Math.random() * 4, moving: false, phase: Math.random() * 6.28, baseX: x, fly: true };
  }
  // Big-wall formation (AC-25): unsmashable walls with a trampoline planted in front of each lane.
  function spawnBigwallRow(z){
    var laneCount = 2 + Math.floor(Math.random() * 2);
    var laneIdxs = [];
    while (laneIdxs.length < laneCount) {
      var li = Math.floor(Math.random() * 5);
      if (laneIdxs.indexOf(li) === -1) laneIdxs.push(li);
    }
    laneIdxs.forEach(function(li){
      if (rampReserve && li === rampReserve.lane) return;
      obstacles.push(buildObstacle('bigwall', LANES[li], z));
      obstacles.push(buildObstacle('tramp', LANES[li], z + TRAMP_LEAD));
    });
  }
  // Ramp unit (AC-19): stepped voxel stairs up to a raised platform with bonus coins on top.
  // Own row (a breather row) + the lane stays reserved (no spawns) while the unit passes (AC-20).
  function spawnRamp(z){
    var laneIdx = 1 + Math.floor(Math.random() * 3); // inner lanes only
    var x = LANES[laneIdx];
    // Tier ×1/×2/×3 (AC-26): constant slope — taller platforms get proportionally longer stairs.
    var tier = 1;
    if (distGame >= RAMP_TIER_AT_M) { var tr2 = Math.random(); tier = tr2 < 0.6 ? 1 : (tr2 < 0.9 ? 2 : 3); }
    var up = RAMP_UP * tier, topH = RAMP_H * tier, steps = 3 * tier;
    var g = new THREE.Group();
    for (var si = 0; si < steps; si++) {
      var sh = topH * (si + 1) / steps;
      var step = new THREE.Mesh(new THREE.BoxGeometry(RAMP_LANE_W, sh, up / steps), matCache.crate);
      step.position.set(0, sh / 2, -(up / steps) * si - up / (2 * steps));
      g.add(step);
    }
    var slab = new THREE.Mesh(new THREE.BoxGeometry(RAMP_LANE_W, topH, RAMP_TOP), matCache.wall);
    slab.position.set(0, topH / 2, -up - RAMP_TOP / 2);
    g.add(slab);
    g.position.set(x, 0, z);
    scene.add(g);
    ramps.push({ mesh: g, x: x, up: up, topH: topH });
    rampReserve = { lane: laneIdx, untilRow: rowCount + Math.ceil((up + RAMP_TOP) / ROW_EVERY) + 1 };
    for (var ci = 0; ci < 2 + tier; ci++) spawnPickup(x, z - up - 3 - ci * 4, topH + 0.9, 'coin');
  }
  function spawnRow(z){
    rowCount++;
    if (rampReserve && rowCount > rampReserve.untilRow) rampReserve = null;
    if (rowCount % GOAL_EVERY_ROWS === 0) { spawnGoal(z); return; }
    if (distGame >= BIGWALL_AT_M && rowCount % BIGWALL_ROW_MOD === BIGWALL_ROW_OFFSET) { spawnBigwallRow(z); return; }
    if (rowCount % RAMP_ROW_MOD === RAMP_ROW_OFFSET) { spawnRamp(z); return; }
    var gapCount = 1 + Math.floor(Math.random() * 2 + (CFG.diffMult < 1 ? 1 : 0));
    var gaps = {};
    var gapN = 0;
    while (gapN < Math.max(gapCount, 2)) {
      var gi = Math.floor(Math.random() * 5);
      if (!gaps[gi]) { gaps[gi] = true; gapN++; }
    }
    LANES.forEach(function(x, i){
      if (rampReserve && i === rampReserve.lane) return; // the ramp lane stays fully clear
      if (gaps[i]) {
        if (Math.random() < 0.45) spawnPickup(x, z);
        return;
      }
      if (Math.random() < 0.35) return;
      obstacles.push(buildObstacle(pickKind(), x, z));
    });
  }
  function spawnPickup(x, z, y, forceType){
    var r = Math.random();
    var type = forceType;
    if (!type) {
      // Power-ups (AC-29) join the pickup pool after POWERUP_AT_M.
      if (distGame >= POWERUP_AT_M) {
        if (r < 0.62) type = 'coin';
        else if (r < 0.76) type = 'clock';
        else if (r < 0.82) type = 'heart';
        else if (r < 0.89) type = 'magnet';
        else if (r < 0.95) type = 'shield';
        else type = 'x2';
      } else {
        type = r < 0.7 ? 'coin' : (r < 0.88 ? 'clock' : 'heart');
      }
      if (mode === 'level' && type === 'clock') type = 'coin'; // level runs never gain time (AC-42)
    }
    var mesh;
    if (type === 'coin') {
      mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.32, 0.1, 12), new THREE.MeshLambertMaterial({ map: coinTex, color: 0xFCD34D }));
      mesh.rotation.z = Math.PI / 2;
    } else {
      var texMap = { clock: clockTex, heart: heartTex, magnet: magnetTex, shield: shieldTex, x2: x2Tex };
      mesh = new THREE.Mesh(new THREE.PlaneGeometry(0.8, 0.8), new THREE.MeshBasicMaterial({ map: texMap[type], transparent: true, side: THREE.DoubleSide }));
    }
    var baseY = typeof y === 'number' ? y : 0.9; // elevated pickups ride ramp platforms (AC-20)
    mesh.position.set(x, baseY, z);
    scene.add(mesh);
    pickups.push({ mesh: mesh, type: type, baseY: baseY });
  }
  function spawnGoal(z){
    var gx = [-3.3, 0, 3.3][Math.floor(Math.random() * 3)];
    var g = new THREE.Group();
    var half = 1.9, height = 1.7;
    [-half, half].forEach(function(dx){
      var post = new THREE.Mesh(new THREE.BoxGeometry(0.16, height, 0.16), matCache.post);
      post.position.set(dx, height / 2, 0); g.add(post);
    });
    var bar = new THREE.Mesh(new THREE.BoxGeometry(half * 2 + 0.16, 0.16, 0.16), matCache.post);
    bar.position.set(0, height, 0); g.add(bar);
    var net = new THREE.Mesh(new THREE.PlaneGeometry(half * 2, height), matCache.net);
    net.position.set(0, height / 2, -0.5); g.add(net);
    g.position.set(gx, 0, z);
    scene.add(g);
    goalGates.push({ mesh: g, half: half, scored: false, nudged: false });
  }

  // ================= RUN LIFECYCLE =================
  var hintTO = null;
  function startRun(){
    // v4: arm the run's mode before resetRun (speed derives from runSpeedMult)
    runSpeedMult = mode === 'level' ? currentLevel().speedMult : 1;
    runSmashes = 0; runSlides = 0; runBounces = 0; levelCleared = false;
    resetRun();
    buildPlayer();
    score = 0; coinsCt = 0; goals = 0; lives = CFG.maxLives; timeLeft = CFG.startSeconds;
    toastMount.innerHTML = '';
    setPaused(false);
    loadMissions(); // day rollover check — mid-day this is a no-op reload
    setScreen('playing');
    renderHud();
    renderChips();
    hintEl.classList.remove('hidden');
    clearTimeout(hintTO);
    hintTO = setTimeout(function(){ hintEl.classList.add('hidden'); }, HINT_MS);
  }
  function endGame(cause){
    var isClear = cause === 'clear';
    sfx(isClear ? 'goal' : 'over');
    var newBest = false;
    if (score > best) {
      newBest = true;
      best = score;
      try { localStorage.setItem(BEST_KEY, String(best)); } catch (e) {}
    }
    el('btnMenu').textContent = mode === 'level' ? 'Level Select' : 'Change Player';
    if (isClear) {
      // LEVEL CLEAR (AC-43/44): award stars (best-ever kept), unlock the next level
      levelCleared = true;
      var lv = currentLevel();
      var earned = levelStars(score, lv.star2, lv.star3);
      if (earned > (levelState.stars[lv.n] || 0)) { levelState.stars[lv.n] = earned; saveLevels(); }
      el('overTitle').textContent = 'LEVEL CLEAR!';
      el('overStars').textContent = starGlyphs(earned);
      el('overStars').classList.remove('hidden');
      el('btnAgain').textContent = levelN < LEVELS.length ? 'Next Level' : 'Play Again';
      burst(playerGroup.position.x, 1.4, 0, 0xFCD34D, 12);
    } else {
      el('overStars').classList.add('hidden');
      el('btnAgain').textContent = mode === 'level' ? 'Retry Level' : 'Play Again';
      el('overTitle').textContent = cause === 'time' ? "TIME'S UP!" : 'OUT OF LIVES!';
    }
    el('overPoints').textContent = String(score);
    el('overCoins').textContent = String(coinsCt);
    el('overGoals').textContent = String(goals);
    el('newBest').classList.toggle('hidden', !newBest);
    renderBestLine();
    renderTrails(); // a new best may have unlocked a trail (AC-39)
    renderMissions();
    setScreen('over');
    window.__post({ type: 'run-end', points: score, coins: coinsCt, goals: goals, distance: Math.floor(distGame), cause: cause });
  }

  // ================= v3: REVIVE (AC-34) — one free offer per run, lives-death only =================
  function offerRevive(){
    setScreen('revive');
    var count = 3;
    el('reviveCount').textContent = '3';
    clearInterval(reviveIv);
    reviveIv = setInterval(function(){
      count -= 1;
      el('reviveCount').textContent = String(Math.max(0, count));
      if (count <= 0) declineRevive();
    }, 1000);
  }
  function acceptRevive(){
    if (screen !== 'revive') return;
    clearInterval(reviveIv); reviveIv = null;
    usedRevive = true;
    lives = 1;
    invincible = 2.5;
    // clear the immediate danger zone so the comeback is fair
    obstacles = obstacles.filter(function(o){
      if (o.mesh.position.z > -14) { scene.remove(o.mesh); return false; }
      return true;
    });
    comboBreak();
    setScreen('playing');
    renderHud();
    showToast('BACK IN THE GAME!', '#6EE7B7');
    burst(playerGroup.position.x, 1, 0, 0x6EE7B7, 10);
  }
  function declineRevive(){
    if (screen !== 'revive') return;
    clearInterval(reviveIv); reviveIv = null;
    usedRevive = true;
    endGame('lives');
  }

  // ================= INPUT =================
  function grounded(){ return py <= support + 0.01 && vy <= 0; }
  function jump(){
    if (grounded() && screen === 'playing' && !paused) { slideT = 0; vy = JUMP_VY; py = support + 0.001; beep(520, 0.1, 'sine', 0.06); }
  }
  function slideMove(){
    if (grounded() && slideT <= 0 && screen === 'playing' && !paused) { slideT = SLIDE_T; beep(300, 0.1, 'sine', 0.06); }
  }
  function kickBall(){
    if (kickT <= 0 && screen === 'playing' && !paused) { kickT = KICK_T; beep(240, 0.12, 'triangle', 0.1); }
  }
  function begin(){
    if (screen !== 'playing') startRun();
  }
  // Keyboard binds on window — must work without canvas focus; arrows/space never scroll (AC-8).
  window.addEventListener('keydown', function(e){
    if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') { e.preventDefault(); steer = -1; }
    if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') { e.preventDefault(); steer = 1; }
    if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
      if (screen === 'playing') { e.preventDefault(); jump(); }
    }
    if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') {
      if (screen === 'playing') { e.preventDefault(); slideMove(); }
    }
    if (e.key === 'x' || e.key === 'X' || e.key === 'f' || e.key === 'F') {
      if (screen === 'playing') { e.preventDefault(); kickBall(); }
    }
    if (e.key === ' ') {
      // Space kicks during play; on menus it still starts the run.
      e.preventDefault();
      if (screen === 'playing') kickBall(); else begin();
    }
    if (e.key === 'Enter' && screen !== 'playing' && screen !== 'revive') { e.preventDefault(); begin(); }
    if (e.key === 'Escape' && screen === 'playing') { e.preventDefault(); setPaused(!paused); }
  });
  // Key-up only zeroes steer if it matches the held direction (both-keys-held stays sane).
  window.addEventListener('keyup', function(e){
    if ((e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') && steer < 0) steer = 0;
    if ((e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') && steer > 0) steer = 0;
  });
  // Control buttons — pointer-leave MUST release steer or the player sticks to a wall.
  function bindSteer(id, dir){
    var b = el(id);
    var down = function(e){ e.preventDefault(); steer = dir; b.classList.add('active'); };
    var up = function(){ if (steer === dir) steer = 0; b.classList.remove('active'); };
    b.addEventListener('pointerdown', down);
    b.addEventListener('pointerup', up);
    b.addEventListener('pointerleave', up);
    b.addEventListener('pointercancel', up);
  }
  bindSteer('btnLeft', -1);
  bindSteer('btnRight', 1);
  function bindTap(id, fn){
    var b = el(id);
    b.addEventListener('pointerdown', function(e){ e.preventDefault(); b.classList.add('active'); fn(); });
    b.addEventListener('pointerup', function(){ b.classList.remove('active'); });
    b.addEventListener('pointerleave', function(){ b.classList.remove('active'); });
  }
  bindTap('btnJump', jump);
  bindTap('btnSlide', slideMove);
  bindTap('btnKick', kickBall);
  el('btnKickOff').addEventListener('click', startRun);
  el('btnAgain').addEventListener('click', function(){
    // in the campaign, the primary button advances after a clear (AC-43)
    if (mode === 'level' && levelCleared && levelN < LEVELS.length) levelN += 1;
    startRun();
  });
  el('btnMenu').addEventListener('click', function(){
    renderBestLine(); renderTrails(); renderMissions(); setMode(mode); setScreen('start');
  });
  el('modeEndless').addEventListener('click', function(){ setMode('endless'); });
  el('modeLevels').addEventListener('click', function(){ setMode('level'); });
  // v3 controls: pause / sound / revive
  el('btnPause').addEventListener('click', function(){ setPaused(true); });
  el('btnResume').addEventListener('click', function(){ setPaused(false); });
  el('btnSound').addEventListener('click', function(){
    muted = !muted;
    try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch (e) {}
    renderSoundBtn();
  });
  el('btnRevive').addEventListener('click', acceptRevive);
  el('btnGiveUp').addEventListener('click', declineRevive);
  document.addEventListener('visibilitychange', function(){
    if (document.hidden && screen === 'playing') setPaused(true); // AC-35 auto-pause
  });
  // Drag anywhere on the field to steer; a quick vertical swipe jumps (up) or slides (down).
  var dragX = null, dragY0 = 0, dragT0 = 0, swiped = false;
  stage.addEventListener('pointerdown', function(e){
    dragX = e.clientX; dragY0 = e.clientY; dragT0 = performance.now(); swiped = false;
    if (e.target && e.target.setPointerCapture) { try { e.target.setPointerCapture(e.pointerId); } catch (err) {} }
  });
  stage.addEventListener('pointermove', function(e){
    if (dragX === null || screen !== 'playing') return;
    var dx = e.clientX - dragX; dragX = e.clientX;
    playerX = Math.max(-PLAYER_CLAMP_X, Math.min(PLAYER_CLAMP_X, playerX + dx * DRAG_STEER));
    var dyTot = e.clientY - dragY0;
    if (!swiped && Math.abs(dyTot) > SWIPE_PX && performance.now() - dragT0 < 350) {
      swiped = true;
      if (dyTot < 0) jump(); else slideMove();
    }
  });
  stage.addEventListener('pointerup', function(){ dragX = null; });
  stage.addEventListener('pointercancel', function(){ dragX = null; });

  // ================= FRAME LOOP =================
  renderer.domElement.addEventListener('webglcontextlost', function(e){ e.preventDefault(); });
  renderer.domElement.addEventListener('webglcontextrestored', function(){ lastT = performance.now(); });

  function tick(dt, t){
    var playing = screen === 'playing' && !paused;
    if (playing) {
      if (!Number.isFinite(playerX)) playerX = 0;
      playerX = Math.max(-PLAYER_CLAMP_X, Math.min(PLAYER_CLAMP_X, playerX + steer * STEER_SPEED * dt));
      speed = Math.min(speed + SPEED_ACCEL * dt * CFG.diffMult * runSpeedMult, SPEED_CAP * CFG.diffMult * runSpeedMult);
    }
    playerGroup.position.x += (playerX - playerGroup.position.x) * Math.min(1, dt * 14);
    playerGroup.rotation.z = (playerX - playerGroup.position.x) * -0.25;
    var runRate = playing ? 11 : 3;
    var swing = Math.sin(t * runRate) * (playing ? 0.7 : 0.15);
    legL.rotation.x = swing; legR.rotation.x = -swing;
    legL.userData.knee.rotation.x = Math.max(0, -Math.sin(t * runRate)) * (playing ? 1.1 : 0.2);
    legR.userData.knee.rotation.x = Math.max(0, Math.sin(t * runRate)) * (playing ? 1.1 : 0.2);
    armL.rotation.x = -swing * 0.8; armR.rotation.x = swing * 0.8;
    // support under the player (ramp inclines/platforms); py is ABSOLUTE height above the pitch
    support = 0;
    for (var ri = 0; ri < ramps.length; ri++) {
      var rs = rampSupportHeight(ramps[ri].x, ramps[ri].mesh.position.z, RAMP_LANE_W, ramps[ri].up, RAMP_TOP, ramps[ri].topH, playerGroup.position.x);
      if (rs > support) support = rs;
    }
    if (slideT > 0) slideT -= dt;
    // vertical physics: gravity above the support, ride the support otherwise (rate-limited climb
    // so lateral platform entry eases up instead of teleporting)
    if (py > support || vy > 0) {
      vy -= GRAVITY * dt;
      py = py + vy * dt;
      if (py <= support && vy <= 0) { py = support; vy = 0; }
    } else if (py < support) {
      py = Math.min(support, py + 14 * dt); vy = 0;
    } else { vy = 0; }
    var air = Math.min(1, Math.max(0, py - support) / 3);
    playerShadow.scale.setScalar(1 - air * 0.5);
    playerShadow.material.opacity = 0.3 - air * 0.2;
    playerShadow.position.y = 0.02 - (py - support);
    playerGroup.position.y = py + Math.abs(Math.sin(t * runRate)) * (playing ? 0.08 : 0.02);
    // slide crouch (AC-17): squash while the slide window is live, restore after
    var squash = slideT > 0 ? 0.45 : 1;
    playerGroup.scale.y += (squash - playerGroup.scale.y) * Math.min(1, dt * 16);
    if (invincible > 0) invincible -= dt;
    playerGroup.visible = invincible > 0 ? (Math.floor(t * 12) % 2 === 0) : true;

    // ball: dribble bob, or fly forward while kicked
    if (kickT > 0) {
      kickT -= dt;
      ball.position.z -= KICK_FLY * dt;
      ball.position.y = 0.42 + Math.sin((1 - kickT / KICK_T) * Math.PI) * KICK_ARC;
      if (kickT <= 0) ball.position.set(playerGroup.position.x + BALL_REST_DX, 0.42, BALL_REST_Z);
    } else {
      ball.position.x += (playerGroup.position.x + BALL_REST_DX - ball.position.x) * Math.min(1, dt * 8);
      ball.position.z = BALL_REST_Z;
      ball.position.y = 0.42 + Math.abs(Math.sin(t * 9)) * (playing ? 0.12 : 0);
    }
    ball.rotation.x -= (playing ? speed : 2) * dt / 0.42;

    // soft camera follow — x eases toward player.x * 0.55, never 1:1; y eases slightly up with
    // player height to sell elevated routes
    camera.position.x += (playerGroup.position.x * 0.55 - camera.position.x) * Math.min(1, dt * 5);
    camera.position.y += ((5.4 + py * 0.45) - camera.position.y) * Math.min(1, dt * 3);
    // v3: screen shake (decaying jitter on top of the follow), particles, trail, theme blend
    if (shakeT > 0 && !reduceMotion) {
      camera.position.x += (Math.random() - 0.5) * shakeT * 0.5;
      camera.position.y += (Math.random() - 0.5) * shakeT * 0.35;
    }
    shakeT = Math.max(0, shakeT - dt);
    updateParticles(dt);
    updateTrail(dt);
    var thIdx = Math.floor(distGame / THEME_EVERY_M) % THEME_BG_C.length;
    scene.background.lerp(THEME_BG_C[thIdx], Math.min(1, dt * 0.8));
    scene.fog.color.copy(scene.background);
    sun.color.lerp(THEME_SUN_C[thIdx], Math.min(1, dt * 0.8));

    if (!playing) return;

    // power-up timers (AC-29)
    if (magnetT > 0) { magnetT -= dt; if (magnetT <= 0) renderChips(); }
    if (x2T > 0) { x2T -= dt; if (x2T <= 0) renderChips(); }

    var dz = speed * dt;
    distGame += dz;
    // Scroll the pitch the same way the obstacles travel (+z, toward the camera) so the ground reads as
    // running forward — the previous sign made the grass stream backward under the player.
    grassTex.offset.y += dz / 8;

    spawnZ += dz;
    while (spawnZ > ROW_EVERY) { spawnRow(-160); spawnZ -= ROW_EVERY; }
    // moving threats on a distance cadence (AC-22, AC-23)
    if (distGame >= nextRunnerM) { nextRunnerM = distGame + 55 + Math.random() * 30; obstacles.push(buildRunner()); }
    if (distGame >= nextRollerM) { nextRollerM = distGame + 45 + Math.random() * 25; obstacles.push(buildRoller()); }
    if (distGame >= nextFlyerM) { nextFlyerM = distGame + 40 + Math.random() * 30; obstacles.push(buildFlyer()); }

    obstacles.forEach(function(o){
      o.mesh.position.z += dz + o.vz * dt;
      if (o.moving) o.mesh.position.x = o.baseX + Math.sin(t * 1.6 + o.phase) * 1.8;
      if (o.legs) { var sw2 = Math.sin(t * 12 + o.phase) * 0.55; o.legs[0].rotation.x = sw2; o.legs[1].rotation.x = -sw2; }
      if (o.kind === 'roller' || o.fly) o.mesh.rotation.x += (speed + o.vz) * dt / 0.4;
      if (o.mesh.userData && o.mesh.userData.flames) {
        o.mesh.userData.flames.forEach(function(f, fi){ f.scale.x = 1 + Math.sin(t * 11 + fi * 1.7) * 0.12; f.scale.y = 0.85 + Math.sin(t * 13 + fi * 2.4) * 0.18; });
      }
      if (o.kind === 'float') o.mesh.position.y = o.gap + o.h / 2 + Math.sin(t * 2.4 + o.phase) * 0.05;
      // v3: the moment an obstacle passes the player unharmed — near-miss combo (AC-31) or a
      // counted slide-under (mission). Trampolines are pads, not threats.
      if (!o.passed && o.mesh.position.z > 0.8) {
        o.passed = true;
        if (!o.bounce) {
          var px2 = playerGroup.position.x;
          if (o.gap > 0 && Math.abs(o.mesh.position.x - px2) < o.w / 2 + 0.35) {
            missionEvent('slides', 1);
            runSlides += 1;
          } else if (isNearMiss(o.mesh.position.x, o.w, px2, py)) {
            comboEvent();
            addPoints(SCORING.NEAR_MISS_POINTS, true);
          }
        }
      }
    });
    pickups.forEach(function(o){
      o.mesh.position.z += dz;
      // magnet (AC-29): nearby coins ease toward the player (and down from platforms)
      if (magnetT > 0 && o.type === 'coin') {
        var mdx = playerGroup.position.x - o.mesh.position.x, mdz = 0 - o.mesh.position.z;
        if (Math.sqrt(mdx * mdx + mdz * mdz) < MAGNET_RADIUS) {
          o.mesh.position.x += mdx * dt * 6;
          o.mesh.position.z += mdz * dt * 6;
          o.baseY += (0.9 + py - o.baseY) * dt * 6;
        }
      }
      if (o.type === 'coin') o.mesh.rotation.y += dt * 4; else o.mesh.rotation.y = Math.sin(t * 3) * 0.5;
      o.mesh.position.y = o.baseY + Math.sin(t * 4 + o.mesh.position.x) * 0.08;
    });
    goalGates.forEach(function(o){ o.mesh.position.z += dz; });
    scrollAdPanels(dz); // the boards travel at the world's pace
    ramps = ramps.filter(function(r){
      r.mesh.position.z += dz;
      if (r.mesh.position.z - (r.up + RAMP_TOP) > 12) { scene.remove(r.mesh); return false; }
      return true;
    });

    // kicked ball smashes blocks and scores from range (predicates: blockyStrikerScoring.ts)
    if (kickT > 0) {
      var bz = ball.position.z, bkx = ball.position.x;
      obstacles = obstacles.filter(function(o){
        if (!o.smashable) return true; // spikes/fire/overhead shrug off the ball (AC-21)
        var m = o.mesh.position;
        if (ballSmashesObstacle(m.x, m.z, o.w, bkx, bz)) {
          scene.remove(o.mesh);
          sfx('coin');
          comboEvent();
          var smashGain = addPoints(SCORING.SMASH_POINTS, true);
          showToast('SMASH! +' + smashGain, '#A78BFA');
          missionEvent('smashes', 1);
          runSmashes += 1;
          burst(m.x, Math.max(0.6, o.h / 2), m.z, 0xA78BFA, 8);
          shakeT = Math.max(shakeT, 0.12);
          kickT = 0.01;
          return false;
        }
        return true;
      });
      goalGates.forEach(function(o){
        var m = o.mesh.position;
        if (!o.scored && ballScoresGoal(m.x, m.z, o.half, bkx, bz, true)) {
          o.scored = true;
          sfx('goal');
          goals += 1;
          comboEvent();
          var goalGain = addPoints(SCORING.GOAL_POINTS, true);
          if (mode !== 'level') timeLeft += SCORING.GOAL_TIME_BONUS_S; // level runs never gain time (AC-42)
          showToast('\\u26BD SCREAMER! +' + goalGain, '#FCD34D');
          missionEvent('goals', 1);
          buzz(20);
          burst(m.x, 1.2, m.z, 0xFCD34D, 10);
          shakeT = Math.max(shakeT, 0.25);
          kickT = 0.01;
        }
      });
    }

    // player vs obstacles
    var ppx = playerGroup.position.x;
    // trampolines first (AC-24): pads bounce, never hurt; vy guard stops re-triggering mid-launch
    for (var bi = 0; bi < obstacles.length; bi++) {
      var pad = obstacles[bi];
      if (!pad.bounce) continue;
      var pm2 = pad.mesh.position;
      if (vy <= 0 && trampolineBounces(pm2.x, pm2.z, pad.w, ppx, py - support)) {
        vy = TRAMP_VY;
        py = support + 0.01;
        slideT = 0;
        sfx('life');
        burst(pm2.x, 0.4, pm2.z, 0x6EE7B7, 6);
        shakeT = Math.max(shakeT, 0.15);
        missionEvent('bounces', 1);
        runBounces += 1;
        break;
      }
    }
    if (invincible <= 0) {
      for (var i = 0; i < obstacles.length; i++) {
        var o = obstacles[i], m = o.mesh.position;
        if (o.bounce) continue; // pads handled above
        var outcome = playerObstacleOutcome(m.x, m.z, o.w, o.h, ppx, py, slideT > 0, o.gap);
        if (outcome === 'hit') {
          if (shieldOn) {
            // shield (AC-29): absorbs the hit, no life lost
            shieldOn = false;
            invincible = INVINCIBLE_S;
            sfx('life');
            showToast('SHIELD SAVED YOU!', '#60A5FA');
            renderChips();
            break;
          }
          invincible = INVINCIBLE_S;
          sfx('hit');
          buzz(40);
          shakeT = Math.max(shakeT, 0.4);
          comboBreak();
          showToast('OUCH!', '#F87171');
          lives -= 1;
          renderHud();
          if (lives <= 0) {
            // revive (AC-34): one free offer per run on a lives death
            if (!usedRevive) { offerRevive(); return; }
            endGame('lives');
            return;
          }
          break;
        }
      }
    }
    // pickups — collected by ball OR player (forgiving)
    var bx = ball.position.x, bz2 = ball.position.z;
    pickups = pickups.filter(function(o){
      var m = o.mesh.position;
      if (collectsPickup(m.x, m.z, ppx, bx, bz2, o.baseY, py)) {
        scene.remove(o.mesh);
        if (o.type === 'coin') {
          sfx('coin'); coinsCt += 1;
          addPoints(SCORING.COIN_POINTS, false);
          missionEvent('coins', 1);
          burst(m.x, o.baseY, m.z, 0xFCD34D, 3);
        }
        if (o.type === 'clock') { sfx('life'); timeLeft += SCORING.CLOCK_TIME_BONUS_S; showToast('+' + SCORING.CLOCK_TIME_BONUS_S + ' SEC', '#6EE7B7'); }
        if (o.type === 'heart') {
          sfx('life');
          if (lives < CFG.maxLives) { lives += 1; showToast('+1 LIFE', '#F87171'); }
          else addPoints(SCORING.HEART_FULL_LIVES_POINTS, false);
        }
        if (o.type === 'magnet') { sfx('life'); magnetT = MAGNET_S; showToast('MAGNET!', '#F87171'); renderChips(); }
        if (o.type === 'shield') { sfx('life'); shieldOn = true; showToast('SHIELD UP!', '#60A5FA'); renderChips(); }
        if (o.type === 'x2') { sfx('life'); x2T = X2_S; showToast('DOUBLE POINTS!', '#FCD34D'); renderChips(); }
        renderHud();
        return false;
      }
      if (m.z > 12) { scene.remove(o.mesh); return false; }
      return true;
    });
    // goals only count on a KICK — dribbling through nudges the mechanic, once per gate
    goalGates = goalGates.filter(function(o){
      var m = o.mesh.position;
      if (!o.scored && !o.nudged && kickT <= 0 && dribbleThroughGate(m.x, m.z, o.half, bx, false)) {
        o.nudged = true;
        showToast('KICK it in to score!', '#9CA3AF');
      }
      if (m.z > 12) { scene.remove(o.mesh); return false; }
      return true;
    });
    obstacles = obstacles.filter(function(o){
      if (o.mesh.position.z > 12) { scene.remove(o.mesh); return false; }
      return true;
    });

    // timer + distance + HUD — throttled to every 250ms, never per frame
    hudTimer += dt;
    if (hudTimer >= HUD_THROTTLE) {
      // no countdown in level mode (AC-42) — a level ends by objective or lives only
      if (mode !== 'level') {
        timeLeft -= hudTimer;
        if (timeLeft <= 0) { timeLeft = 0; hudTimer = 0; renderHud(); endGame('time'); return; }
      }
      hudTimer = 0;
      while (distGame >= nextDistScore) { nextDistScore += SCORING.DIST_STEP_M; addPoints(SCORING.DIST_STEP_POINTS, true); }
      missionEvent('distance', Math.floor(distGame));
      // level objective check (AC-43): meeting it ends the run right here as a clear
      if (mode === 'level' && !levelCleared && levelObjectiveMet(currentLevel().objective, currentRunStats())) {
        renderHud();
        endGame('clear');
        return;
      }
      renderHud();
      renderChips();
    }
  }

  var lastT = performance.now();
  var lastStepAt = lastT; // wall-clock of the last real step (rAF or watchdog) — drives the stall watchdog
  var raf = null;
  function step(t){
    lastStepAt = performance.now();
    try {
      // self-heal: if the canvas got detached (host re-render), re-attach and re-size
      if (!renderer.domElement.isConnected) {
        stage.appendChild(renderer.domElement);
        sizeToStage();
      }
      var dt = Math.min((t - lastT) / 1000, MAX_DT); lastT = t;
      tick(dt, t / 1000);
      renderer.render(scene, camera);
    } catch (frameErr) {
      // One bad frame must not kill the run — log it, skip the frame, keep the loop alive.
      try { window.__errs.push('frame: ' + String(frameErr && frameErr.stack || frameErr)); } catch (e2) {}
    }
  }
  function loop(t){
    raf = requestAnimationFrame(loop);
    step(t);
  }

  // ================= BOOT =================
  renderAvatars();
  renderBestLine();
  loadMissions();
  renderTrails();
  renderSoundBtn();
  setMode(mode); // apply the persisted (or default: Levels) mode to the picker + grid
  setScreen('start');
  renderer.render(scene, camera); // prove the pipeline before declaring ready
  raf = requestAnimationFrame(loop);
  // rAF watchdog. The browser throttles/pauses requestAnimationFrame not only for hidden tabs but
  // for any frame it deems non-rendered — occluded, or embedded in an iframe/WebView the host has
  // sized/positioned oddly — while the tab still reports visible. A document.hidden-only fallback
  // misses that, so the run freezes on frame one with nothing driving the loop. Instead drive step()
  // from a timer whenever rAF hasn't ticked in >WATCHDOG_MS; when rAF is healthy this never fires,
  // and movement is dt-based so a rare overlap tick is harmless. rAF seamlessly retakes the loop the
  // instant it resumes.
  var WATCHDOG_MS = 24;
  setInterval(function(){
    if (performance.now() - lastStepAt >= WATCHDOG_MS) step(performance.now());
  }, 16);
  window.__post({ type: 'ready' });

  } catch (err) {
    try { window.__errs.push(String(err && err.stack || err)); } catch (e2) {}
    window.__post({ type: 'load-error' });
  }
})();
</script>
</body>
</html>`;
}
